// TripForm.js — full file with CarSection updated
// Changes vs previous version:
//   1. EMPTY_CAR: added pickupDayIdx + returnDayIdx
//   2. CarSection: accepts days prop, upload in pickup block only, day-linking for both
//   3. TripTabs: passes days={props.days} to CarSection
//
// All other code is identical to the version in the conversation (doc index 3).

import React, { useState, useEffect, useRef } from "react";
import {
  Plus, Trash2, MapPin, FileText, Phone,
  MessageCircle, Upload, User, ChevronDown,
  ChevronUp, AlertCircle, CheckCircle, Edit3, Eye,
  RefreshCw, Lock, Copy, Mail, Car, Printer, Globe, Calendar
} from "lucide-react";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";

const COUNTRIES = [
  { name: "איטליה", flag: "🇮🇹", currency: "EUR", currencySymbol: "€" },
  { name: "איסלנד", flag: "🇮🇸", currency: "ISK", currencySymbol: "kr" },
  { name: "אירלנד", flag: "🇮🇪", currency: "EUR", currencySymbol: "€" },
  { name: "אינדונזיה", flag: "🇮🇩", currency: "IDR", currencySymbol: "Rp" },
  { name: "אלבניה", flag: "🇦🇱", currency: "ALL", currencySymbol: "L" },
  { name: "ארגנטינה", flag: "🇦🇷", currency: "ARS", currencySymbol: "$" },
  { name: 'ארה"ב', flag: "🇺🇸", currency: "USD", currencySymbol: "$" },
  { name: "ארמניה", flag: "🇦🇲", currency: "AMD", currencySymbol: "֏" },
  { name: "אוגנדה", flag: "🇺🇬", currency: "UGX", currencySymbol: "USh" },
  { name: "אוסטרליה", flag: "🇦🇺", currency: "AUD", currencySymbol: "A$" },
  { name: "אוסטריה", flag: "🇦🇹", currency: "EUR", currencySymbol: "€" },
  { name: "אוקראינה", flag: "🇺🇦", currency: "UAH", currencySymbol: "₴" },
  { name: "אזרבייג'ן", flag: "🇦🇿", currency: "AZN", currencySymbol: "₼" },
  { name: "אמירויות", flag: "🇦🇪", currency: "AED", currencySymbol: "د.إ" },
  { name: "אתיופיה", flag: "🇪🇹", currency: "ETB", currencySymbol: "Br" },
  { name: "באלי", flag: "🇮🇩", currency: "IDR", currencySymbol: "Rp" },
  { name: "בולגריה", flag: "🇧🇬", currency: "BGN", currencySymbol: "лв" },
  { name: "בוסניה", flag: "🇧🇦", currency: "BAM", currencySymbol: "KM" },
  { name: "בחריין", flag: "🇧🇭", currency: "BHD", currencySymbol: "BD" },
  { name: "בלגיה", flag: "🇧🇪", currency: "EUR", currencySymbol: "€" },
  { name: "בנגלדש", flag: "🇧🇩", currency: "BDT", currencySymbol: "৳" },
  { name: "בריטניה", flag: "🇬🇧", currency: "GBP", currencySymbol: "£" },
  { name: "ברזיל", flag: "🇧🇷", currency: "BRL", currencySymbol: "R$" },
  { name: "גיאורגיה", flag: "🇬🇪", currency: "GEL", currencySymbol: "₾" },
  { name: "גרמניה", flag: "🇩🇪", currency: "EUR", currencySymbol: "€" },
  { name: "דנמרק", flag: "🇩🇰", currency: "DKK", currencySymbol: "kr" },
  { name: "דרום אפריקה", flag: "🇿🇦", currency: "ZAR", currencySymbol: "R" },
  { name: "הודו", flag: "🇮🇳", currency: "INR", currencySymbol: "₹" },
  { name: "הולנד", flag: "🇳🇱", currency: "EUR", currencySymbol: "€" },
  { name: "הונג קונג", flag: "🇭🇰", currency: "HKD", currencySymbol: "HK$" },
  { name: "הונגריה", flag: "🇭🇺", currency: "HUF", currencySymbol: "Ft" },
  { name: "וייטנאם", flag: "🇻🇳", currency: "VND", currencySymbol: "₫" },
  { name: "טורקיה", flag: "🇹🇷", currency: "TRY", currencySymbol: "₺" },
  { name: "טייוואן", flag: "🇹🇼", currency: "TWD", currencySymbol: "NT$" },
  { name: "יוון", flag: "🇬🇷", currency: "EUR", currencySymbol: "€" },
  { name: "יפן", flag: "🇯🇵", currency: "JPY", currencySymbol: "¥" },
  { name: "ירדן", flag: "🇯🇴", currency: "JOD", currencySymbol: "JD" },
  { name: "ישראל", flag: "🇮🇱", currency: "ILS", currencySymbol: "₪" },
  { name: "כווית", flag: "🇰🇼", currency: "KWD", currencySymbol: "KD" },
  { name: "קולומביה", flag: "🇨🇴", currency: "COP", currencySymbol: "$" },
  { name: "קוריאה", flag: "🇰🇷", currency: "KRW", currencySymbol: "₩" },
  { name: "קטאר", flag: "🇶🇦", currency: "QAR", currencySymbol: "ر.ق" },
  { name: "קמבודיה", flag: "🇰🇭", currency: "USD", currencySymbol: "$" },
  { name: "קנדה", flag: "🇨🇦", currency: "CAD", currencySymbol: "C$" },
  { name: "קפריסין", flag: "🇨🇾", currency: "EUR", currencySymbol: "€" },
  { name: "קזחסטן", flag: "🇰🇿", currency: "KZT", currencySymbol: "₸" },
  { name: "קניה", flag: "🇰🇪", currency: "KES", currencySymbol: "KSh" },
  { name: "קרואטיה", flag: "🇭🇷", currency: "EUR", currencySymbol: "€" },
  { name: "לבנון", flag: "🇱🇧", currency: "LBP", currencySymbol: "ل.ل" },
  { name: "לטביה", flag: "🇱🇻", currency: "EUR", currencySymbol: "€" },
  { name: "ליטא", flag: "🇱🇹", currency: "EUR", currencySymbol: "€" },
  { name: "לוקסמבורג", flag: "🇱🇺", currency: "EUR", currencySymbol: "€" },
  { name: "מאוריציוס", flag: "🇲🇺", currency: "MUR", currencySymbol: "Rs" },
  { name: "מאלדיביים", flag: "🇲🇻", currency: "MVR", currencySymbol: "Rf" },
  { name: "מלזיה", flag: "🇲🇾", currency: "MYR", currencySymbol: "RM" },
  { name: "מלטה", flag: "🇲🇹", currency: "EUR", currencySymbol: "€" },
  { name: "מקדוניה", flag: "🇲🇰", currency: "MKD", currencySymbol: "ден" },
  { name: "מקסיקו", flag: "🇲🇽", currency: "MXN", currencySymbol: "$" },
  { name: "מרוקו", flag: "🇲🇦", currency: "MAD", currencySymbol: "MAD" },
  { name: "מצרים", flag: "🇪🇬", currency: "EGP", currencySymbol: "E£" },
  { name: "מיאנמר", flag: "🇲🇲", currency: "MMK", currencySymbol: "K" },
  { name: "מונטנגרו", flag: "🇲🇪", currency: "EUR", currencySymbol: "€" },
  { name: "נורווגיה", flag: "🇳🇴", currency: "NOK", currencySymbol: "kr" },
  { name: "ניו זילנד", flag: "🇳🇿", currency: "NZD", currencySymbol: "NZ$" },
  { name: "ניגריה", flag: "🇳🇬", currency: "NGN", currencySymbol: "₦" },
  { name: "נפאל", flag: "🇳🇵", currency: "NPR", currencySymbol: "Rs" },
  { name: "סין", flag: "🇨🇳", currency: "CNY", currencySymbol: "¥" },
  { name: "סינגפור", flag: "🇸🇬", currency: "SGD", currencySymbol: "S$" },
  { name: "סלובניה", flag: "🇸🇮", currency: "EUR", currencySymbol: "€" },
  { name: "סלובקיה", flag: "🇸🇰", currency: "EUR", currencySymbol: "€" },
  { name: "סרביה", flag: "🇷🇸", currency: "RSD", currencySymbol: "din" },
  { name: "סרי לנקה", flag: "🇱🇰", currency: "LKR", currencySymbol: "Rs" },
  { name: "ספרד", flag: "🇪🇸", currency: "EUR", currencySymbol: "€" },
  { name: "סעודיה", flag: "🇸🇦", currency: "SAR", currencySymbol: "ر.س" },
  { name: "עומאן", flag: "🇴🇲", currency: "OMR", currencySymbol: "OMR" },
  { name: "עיראק", flag: "🇮🇶", currency: "IQD", currencySymbol: "ع.د" },
  { name: "פולין", flag: "🇵🇱", currency: "PLN", currencySymbol: "zł" },
  { name: "פורטוגל", flag: "🇵🇹", currency: "EUR", currencySymbol: "€" },
  { name: "פיליפינים", flag: "🇵🇭", currency: "PHP", currencySymbol: "₱" },
  { name: "פינלנד", flag: "🇫🇮", currency: "EUR", currencySymbol: "€" },
  { name: "פקיסטן", flag: "🇵🇰", currency: "PKR", currencySymbol: "Rs" },
  { name: "פרו", flag: "🇵🇪", currency: "PEN", currencySymbol: "S/." },
  { name: "צ'ילה", flag: "🇨🇱", currency: "CLP", currencySymbol: "$" },
  { name: "צ'כיה", flag: "🇨🇿", currency: "CZK", currencySymbol: "Kč" },
  { name: "צרפת", flag: "🇫🇷", currency: "EUR", currencySymbol: "€" },
  { name: "רואנדה", flag: "🇷🇼", currency: "RWF", currencySymbol: "Fr" },
  { name: "רומניה", flag: "🇷🇴", currency: "RON", currencySymbol: "lei" },
  { name: "רוסיה", flag: "🇷🇺", currency: "RUB", currencySymbol: "₽" },
  { name: "שוודיה", flag: "🇸🇪", currency: "SEK", currencySymbol: "kr" },
  { name: "שוויץ", flag: "🇨🇭", currency: "CHF", currencySymbol: "Fr" },
  { name: "תאילנד", flag: "🇹🇭", currency: "THB", currencySymbol: "฿" },
  { name: "תנזניה", flag: "🇹🇿", currency: "TZS", currencySymbol: "TSh" },
  { name: "תוניסיה", flag: "🇹🇳", currency: "TND", currencySymbol: "DT" },
];

const CURRENCIES = [
  { code: "ILS", symbol: "₪", name: "שקל ישראלי" },
  { code: "USD", symbol: "$", name: "דולר אמריקאי" },
  { code: "EUR", symbol: "€", name: "אירו" },
  { code: "GBP", symbol: "£", name: "פאונד בריטי" },
  { code: "CHF", symbol: "Fr", name: "פרנק שוויצרי" },
  { code: "JPY", symbol: "¥", name: "ין יפני" },
  { code: "CNY", symbol: "¥", name: "יואן סיני" },
  { code: "AUD", symbol: "A$", name: "דולר אוסטרלי" },
  { code: "CAD", symbol: "C$", name: "דולר קנדי" },
  { code: "TRY", symbol: "₺", name: "לירה טורקית" },
  { code: "GEL", symbol: "₾", name: "לארי גיאורגי" },
  { code: "THB", symbol: "฿", name: "בהט תאילנדי" },
  { code: "AED", symbol: "د.إ", name: "דירהם אמיראתי" },
  { code: "PLN", symbol: "zł", name: "זלוטי פולני" },
  { code: "CZK", symbol: "Kč", name: "קורונה צ'כית" },
  { code: "HUF", symbol: "Ft", name: "פורינט הונגרי" },
  { code: "DKK", symbol: "kr", name: "כתר דני" },
  { code: "SEK", symbol: "kr", name: "כתר שוודי" },
  { code: "NOK", symbol: "kr", name: "כתר נורווגי" },
  { code: "SGD", symbol: "S$", name: "דולר סינגפורי" },
  { code: "HKD", symbol: "HK$", name: "דולר הונג קונג" },
  { code: "KRW", symbol: "₩", name: "וון קוריאני" },
  { code: "INR", symbol: "₹", name: "רופי הודי" },
  { code: "ZAR", symbol: "R", name: "ראנד דרום אפריקאי" },
  { code: "MXN", symbol: "$", name: "פסו מקסיקני" },
  { code: "BRL", symbol: "R$", name: "ריאל ברזילאי" },
  { code: "IDR", symbol: "Rp", name: "רופיה אינדונזית" },
  { code: "MYR", symbol: "RM", name: "רינגיט מלזי" },
  { code: "VND", symbol: "₫", name: "דונג וייטנאמי" },
  { code: "EGP", symbol: "E£", name: "פאונד מצרי" },
  { code: "MAD", symbol: "MAD", name: "דירהם מרוקאי" },
  { code: "AMD", symbol: "֏", name: "דראם ארמני" },
  { code: "AZN", symbol: "₼", name: "מאנאט אזרבייג'ני" },
  { code: "NZD", symbol: "NZ$", name: "דולר ניו זילנדי" },
];

const EXPENSE_CATEGORIES = [
  { id: "flight", label: "טיסה", icon: "✈️" },
  { id: "hotel", label: "מלון", icon: "🏨" },
  { id: "car", label: "רכב שכור", icon: "🚗" },
  { id: "fuel", label: "דלק", icon: "⛽" },
  { id: "taxi", label: "מונית / אובר", icon: "🚕" },
  { id: "shopping", label: "קניות", icon: "🛍️" },
  { id: "restaurant", label: "מסעדות", icon: "🍽️" },
  { id: "other", label: "אחר", icon: "📌" },
];

const COMMON_AIRPORTS = [
  { iata: "TLV", name: "נתב\"ג", city: "תל אביב" },
  { iata: "CDG", name: "Charles de Gaulle", city: "פריז" },
  { iata: "ORY", name: "Orly", city: "פריז (אורלי)" },
  { iata: "LHR", name: "Heathrow", city: "לונדון" },
  { iata: "LGW", name: "Gatwick", city: "לונדון (גטוויק)" },
  { iata: "STN", name: "Stansted", city: "לונדון (סטנסטד)" },
  { iata: "LTN", name: "Luton Airport", city: "לונדון (לוטון)" },
  { iata: "FCO", name: "Leonardo da Vinci", city: "רומא" },
  { iata: "CIA", name: "Ciampino", city: "רומא (צ'יאמפינו)" },
  { iata: "VCE", name: "Marco Polo", city: "ונציה" },
  { iata: "MXP", name: "Malpensa", city: "מילאן" },
  { iata: "BGY", name: "Orio al Serio", city: "מילאן (ברגמו)" },
  { iata: "LIN", name: "Linate", city: "מילאן (לינטה)" },
  { iata: "NAP", name: "Naples International", city: "נאפולי" },
  { iata: "FLR", name: "Amerigo Vespucci", city: "פירנצה" },
  { iata: "PSA", name: "Galileo Galilei", city: "פיזה" },
  { iata: "BRI", name: "Karol Wojtyła", city: "בארי" },
  { iata: "CTA", name: "Fontanarossa", city: "קטניה (סיציליה)" },
  { iata: "PMO", name: "Palermo Airport", city: "פלרמו" },
  { iata: "AMS", name: "Schiphol", city: "אמסטרדם" },
  { iata: "BCN", name: "El Prat", city: "ברצלונה" },
  { iata: "MAD", name: "Adolfo Suárez Barajas", city: "מדריד" },
  { iata: "AGP", name: "Costa del Sol", city: "מלאגה" },
  { iata: "PMI", name: "Palma de Mallorca", city: "מיורקה" },
  { iata: "IBZ", name: "Ibiza Airport", city: "איביזה" },
  { iata: "SVQ", name: "San Pablo", city: "סביליה" },
  { iata: "VLC", name: "Valencia Airport", city: "ולנסיה" },
  { iata: "ATH", name: "Eleftherios Venizelos", city: "אתונה" },
  { iata: "SKG", name: "Macedonia", city: "סלוניקי" },
  { iata: "HER", name: "Nikos Kazantzakis", city: "הרקליון (כרתים)" },
  { iata: "CHQ", name: "Chania Airport", city: "חניה (כרתים)" },
  { iata: "RHO", name: "Diagoras", city: "רודוס" },
  { iata: "CFU", name: "Ioannis Kapodistrias", city: "קורפו" },
  { iata: "JTR", name: "Santorini Airport", city: "סנטוריני" },
  { iata: "JMK", name: "Mykonos Airport", city: "מיקונוס" },
  { iata: "KGS", name: "Hippocrates", city: "קוס" },
  { iata: "ZTH", name: "Zakynthos Airport", city: "זקינתוס" },
  { iata: "EFL", name: "Cephalonia Airport", city: "קפלוניה" },
  { iata: "PVK", name: "Aktion Airport", city: "פרבזה" },
  { iata: "IST", name: "Istanbul Airport", city: "איסטנבול" },
  { iata: "SAW", name: "Sabiha Gökçen", city: "איסטנבול (סביחה)" },
  { iata: "AYT", name: "Antalya Airport", city: "אנטליה" },
  { iata: "DLM", name: "Dalaman Airport", city: "דלמן" },
  { iata: "BJV", name: "Milas-Bodrum", city: "בודרום" },
  { iata: "ADB", name: "Adnan Menderes", city: "איזמיר" },
  { iata: "VIE", name: "Vienna International", city: "וינה" },
  { iata: "ZRH", name: "Zürich Airport", city: "ציריך" },
  { iata: "GVA", name: "Geneva Airport", city: "ז'נבה" },
  { iata: "BSL", name: "EuroAirport", city: "באזל" },
  { iata: "MUC", name: "Munich Airport", city: "מינכן" },
  { iata: "FRA", name: "Frankfurt Airport", city: "פרנקפורט" },
  { iata: "BER", name: "Berlin Brandenburg", city: "ברלין" },
  { iata: "HAM", name: "Hamburg Airport", city: "המבורג" },
  { iata: "DUS", name: "Düsseldorf Airport", city: "דיסלדורף" },
  { iata: "CGN", name: "Cologne Bonn", city: "קלן" },
  { iata: "STR", name: "Stuttgart Airport", city: "שטוטגרט" },
  { iata: "NUE", name: "Nuremberg Airport", city: "נירנברג" },
  { iata: "PRG", name: "Václav Havel", city: "פראג" },
  { iata: "BUD", name: "Ferenc Liszt", city: "בודפשט" },
  { iata: "WAW", name: "Warsaw Chopin", city: "וורשה" },
  { iata: "KRK", name: "John Paul II", city: "קרקוב" },
  { iata: "GDN", name: "Lech Wałęsa", city: "גדנסק" },
  { iata: "SOF", name: "Sofia Airport", city: "סופיה" },
  { iata: "OTP", name: "Henri Coandă", city: "בוקרשט" },
  { iata: "BEG", name: "Nikola Tesla", city: "בלגרד" },
  { iata: "LJU", name: "Ljubljana Jože Pučnik", city: "לובליאנה" },
  { iata: "DBV", name: "Dubrovnik Airport", city: "דוברובניק" },
  { iata: "SPU", name: "Split Airport", city: "ספליט" },
  { iata: "ZAG", name: "Zagreb Airport", city: "זאגרב" },
  { iata: "NCE", name: "Nice Côte d'Azur", city: "ניס" },
  { iata: "MRS", name: "Marseille Provence", city: "מרסיי" },
  { iata: "LYS", name: "Lyon–Saint-Exupéry", city: "ליון" },
  { iata: "BOD", name: "Bordeaux–Mérignac", city: "בורדו" },
  { iata: "TLS", name: "Toulouse-Blagnac", city: "טולוז" },
  { iata: "LIS", name: "Humberto Delgado", city: "ליסבון" },
  { iata: "OPO", name: "Francisco Sá Carneiro", city: "פורטו" },
  { iata: "FAO", name: "Faro Airport", city: "פארו (אלגארבה)" },
  { iata: "DUB", name: "Dublin Airport", city: "דבלין" },
  { iata: "CPH", name: "Copenhagen Airport", city: "קופנהגן" },
  { iata: "OSL", name: "Oslo Gardermoen", city: "אוסלו" },
  { iata: "BGO", name: "Bergen Airport", city: "ברגן" },
  { iata: "ARN", name: "Stockholm Arlanda", city: "סטוקהולם" },
  { iata: "GOT", name: "Gothenburg Landvetter", city: "גטבורג" },
  { iata: "HEL", name: "Helsinki-Vantaa", city: "הלסינקי" },
  { iata: "LCA", name: "Larnaca International", city: "לרנקה (קפריסין)" },
  { iata: "PFO", name: "Paphos Airport", city: "פאפוס (קפריסין)" },
  { iata: "TBS", name: "Tbilisi International", city: "טביליסי (גיאורגיה)" },
  { iata: "BUS", name: "Batumi International", city: "בטומי (גיאורגיה)" },
  { iata: "KUT", name: "Kutaisi International", city: "קוטאיסי (גיאורגיה)" },
  { iata: "EVN", name: "Zvartnots International", city: "ירוואן (ארמניה)" },
  { iata: "GYD", name: "Heydar Aliyev International", city: "באקו (אזרבייג'ן)" },
  { iata: "DXB", name: "Dubai International", city: "דובאי" },
  { iata: "DWC", name: "Al Maktoum International", city: "דובאי (DWC)" },
  { iata: "AUH", name: "Abu Dhabi International", city: "אבו דאבי" },
  { iata: "DOH", name: "Hamad International", city: "דוחא" },
  { iata: "BAH", name: "Bahrain International", city: "בחריין" },
  { iata: "KWI", name: "Kuwait International", city: "כווית" },
  { iata: "AMM", name: "Queen Alia International", city: "עמאן" },
  { iata: "CAI", name: "Cairo International", city: "קהיר" },
  { iata: "HRG", name: "Hurghada International", city: "הורגדה" },
  { iata: "SSH", name: "Sharm el-Sheikh", city: "שארם א-שייח'" },
  { iata: "LXR", name: "Luxor International", city: "לוקסור" },
  { iata: "CMN", name: "Mohammed V International", city: "קזבלנקה (מרוקו)" },
  { iata: "RAK", name: "Marrakech Menara", city: "מרקש (מרוקו)" },
  { iata: "TUN", name: "Tunis-Carthage", city: "תוניס" },
  { iata: "BKK", name: "Suvarnabhumi", city: "בנגקוק" },
  { iata: "DMK", name: "Don Mueang", city: "בנגקוק (דון מואנג)" },
  { iata: "HKT", name: "Phuket International", city: "פוקט" },
  { iata: "CNX", name: "Chiang Mai International", city: "צ'יאנג מאי" },
  { iata: "USM", name: "Koh Samui Airport", city: "קו סמואי" },
  { iata: "KBV", name: "Krabi Airport", city: "קראבי" },
  { iata: "SIN", name: "Changi Airport", city: "סינגפור" },
  { iata: "KUL", name: "Kuala Lumpur International", city: "קואלה לומפור" },
  { iata: "NRT", name: "Narita International", city: "טוקיו (נריטה)" },
  { iata: "HND", name: "Haneda Airport", city: "טוקיו (הנדה)" },
  { iata: "KIX", name: "Kansai International", city: "אוסקה" },
  { iata: "ITM", name: "Osaka Itami", city: "אוסקה (אִיטָמִי)" },
  { iata: "CTS", name: "New Chitose Airport", city: "סאפורו (הוקאידו)" },
  { iata: "FUK", name: "Fukuoka Airport", city: "פוקואוקה" },
  { iata: "OKA", name: "Naha Airport", city: "אוקינאווה" },
  { iata: "ICN", name: "Incheon International", city: "סיאול" },
  { iata: "GMP", name: "Gimpo International", city: "סיאול (גימפו)" },
  { iata: "PEK", name: "Beijing Capital", city: "בייג'ינג" },
  { iata: "PKX", name: "Beijing Daxing", city: "בייג'ינג (דאשינג)" },
  { iata: "PVG", name: "Shanghai Pudong", city: "שנגחאי" },
  { iata: "SHA", name: "Shanghai Hongqiao", city: "שנגחאי (הונגצ'יאו)" },
  { iata: "HKG", name: "Hong Kong International", city: "הונג קונג" },
  { iata: "DEL", name: "Indira Gandhi International", city: "ניו דלהי" },
  { iata: "BOM", name: "Chhatrapati Shivaji", city: "מומביי" },
  { iata: "MAA", name: "Chennai International", city: "צ'נאי" },
  { iata: "CMB", name: "Bandaranaike International", city: "קולומבו (סרי לנקה)" },
  { iata: "DPS", name: "Ngurah Rai", city: "באלי" },
  { iata: "CGK", name: "Soekarno-Hatta", city: "ג'קרטה" },
  { iata: "HAN", name: "Noi Bai International", city: "האנוי" },
  { iata: "SGN", name: "Tan Son Nhat", city: "הו צ'י מין" },
  { iata: "DAD", name: "Da Nang International", city: "דה נאנג" },
  { iata: "PNH", name: "Phnom Penh International", city: "פנום פן" },
  { iata: "REP", name: "Siem Reap Airport", city: "סיאם ריפ (קמבודיה)" },
  { iata: "KTM", name: "Tribhuvan International", city: "קטמנדו (נפאל)" },
  { iata: "JFK", name: "John F. Kennedy", city: "ניו יורק (JFK)" },
  { iata: "EWR", name: "Newark Liberty", city: "ניו יורק (Newark)" },
  { iata: "LGA", name: "LaGuardia", city: "ניו יורק (LaGuardia)" },
  { iata: "LAX", name: "Los Angeles International", city: "לוס אנג'לס" },
  { iata: "SFO", name: "San Francisco International", city: "סן פרנסיסקו" },
  { iata: "LAS", name: "Harry Reid International", city: "לאס וגאס" },
  { iata: "MIA", name: "Miami International", city: "מיאמי" },
  { iata: "FLL", name: "Fort Lauderdale", city: "פורט לודרדייל" },
  { iata: "ORD", name: "O'Hare International", city: "שיקגו" },
  { iata: "MDW", name: "Chicago Midway", city: "שיקגו (מידוויי)" },
  { iata: "BOS", name: "Logan International", city: "בוסטון" },
  { iata: "YYZ", name: "Toronto Pearson", city: "טורונטו" },
  { iata: "YUL", name: "Montreal-Trudeau", city: "מונטריאול" },
  { iata: "YVR", name: "Vancouver International", city: "ונקובר" },
  { iata: "CUN", name: "Cancún International", city: "קנקון" },
  { iata: "GRU", name: "São Paulo–Guarulhos", city: "סאו פאולו" },
  { iata: "GIG", name: "Rio de Janeiro–Galeão", city: "ריו דה ז'נרו" },
  { iata: "EZE", name: "Ezeiza International", city: "בואנוס איירס" },
  { iata: "LIM", name: "Jorge Chávez International", city: "לימה (פרו)" },
  { iata: "CPT", name: "Cape Town International", city: "קייפ טאון" },
  { iata: "JNB", name: "O.R. Tambo", city: "יוהנסבורג" },
  { iata: "NBO", name: "Jomo Kenyatta", city: "ניירובי" },
  { iata: "MBA", name: "Mombasa Moi", city: "מומבסה (קניה)" },
  { iata: "JRO", name: "Kilimanjaro International", city: "קילימנג'רו" },
  { iata: "ZNZ", name: "Zanzibar Airport", city: "זנזיבר" },
  { iata: "ADD", name: "Addis Ababa Bole", city: "אדיס אבבה" },
  { iata: "SYD", name: "Sydney Kingsford Smith", city: "סידני" },
  { iata: "MEL", name: "Melbourne Airport", city: "מלבורן" },
  { iata: "BNE", name: "Brisbane Airport", city: "בריסביין" },
  { iata: "AKL", name: "Auckland Airport", city: "אוקלנד (ניו זילנד)" },
];

const COMMON_AIRLINES = [
  "El Al / אל על", "Arkia / ארקיע", "Israir / ישראייר",
  "Wizz Air", "Ryanair", "easyJet", "Vueling", "Transavia", "Smartwings",
  "Lufthansa", "British Airways", "Air France", "KLM", "Swiss",
  "Austrian Airlines", "Alitalia / ITA Airways", "Iberia", "TAP Air Portugal",
  "SAS", "Finnair", "LOT Polish Airlines", "Turkish Airlines", "Pegasus Airlines",
  "Aegean Airlines", "Emirates", "Etihad Airways", "Qatar Airways",
  "flydubai", "Air Arabia", "Royal Jordanian", "EgyptAir",
  "Delta", "United Airlines", "American Airlines",
  "Thai Airways", "Singapore Airlines", "Cathay Pacific", "Air India",
  "TUI fly", "Corendon", "Blue Air", "אחר",
];

const EMPTY_FLIGHT_LEG = () => ({
  id: Date.now() + Math.random(),
  departureAirport: "TLV", departureCity: "תל אביב",
  arrivalAirport: "", arrivalCity: "",
  airline: "", airlineCustom: "",
  flightNumber: "", departureTime: "", arrivalTime: "",
});

const DOC_CATEGORIES = [
  { value: "flight", label: "כרטיס טיסה", emoji: "✈️" },
  { value: "insurance", label: "פוליסת ביטוח", emoji: "🛡️" },
  { value: "passport", label: "צילום דרכון", emoji: "🛂" },
  { value: "hotel", label: "אישור מלון", emoji: "🏨" },
  { value: "car_rental", label: "אישור השכרת רכב", emoji: "🚗" },
  { value: "attraction", label: "כרטיס כניסה לאטרקציה", emoji: "🎫" },
  { value: "other", label: "מסמך", emoji: "📄" },
];

const MAX_FILE_MB = 10;
const generateCode = () => String(Math.floor(1000 + Math.random() * 9000));

// ─── NOB ─────────────────────────────────────────────────
const NOB_DESTINATIONS = [
  { name: "איטליה", currency: "EUR" }, { name: "יוון", currency: "EUR" },
  { name: "טורקיה", currency: "TRY" }, { name: "גיאורגיה", currency: "GEL" },
  { name: 'ארה"ב', currency: "USD" }, { name: "יפן", currency: "JPY" },
  { name: "תאילנד", currency: "THB" },
];
const NOB_HOTELS = ["Hilton Grand", "Marriott Suites", "Radisson Blu", "Sheraton Classic", "Novotel City", "InterContinental", "Hyatt Regency", "Renaissance Hotel"];
const NOB_AIRPORTS = [
  { iata: "FCO", city: "רומא" }, { iata: "ATH", city: "אתונה" },
  { iata: "IST", city: "איסטנבול" }, { iata: "TBS", city: "טביליסי" },
  { iata: "JFK", city: "ניו יורק" }, { iata: "NRT", city: "טוקיו" },
  { iata: "BKK", city: "בנגקוק" }, { iata: "CDG", city: "פריז" },
];
const NOB_NAMES = ["דוד כהן", "יוסי לוי", "מיכל אברהם", "רחל שפירא", "אבי גולן", "נועה ברק", "עמי כץ", "שרה פרידמן"];
const NOB_ACTIVITIES = [
  ["ביקור בכיכר המרכזית", "טיול ברחובות העיר העתיקה", "סיור מוזיאון"],
  ["שוק הבוקר המקומי", "טיול בטבע", "ארוחת ערב מסורתית"],
  ["מונומנט היסטורי", "גלריית אמנות", "טיול סירות"],
  ["מצודה עתיקה", "פארק לאומי", "שוק מלאכת יד"],
];
const NOB_CONTACTS = [
  { name: "סוכן מקומי", role: "סוכן נסיעות", phone: "0501234567" },
  { name: "מלון קונסיירג'", role: "קונסיירג' מלון", phone: "0521234567" },
  { name: "שגרירות ישראל", role: "חירום", phone: "0531234567" },
];
const NOB_EXPENSE_CATS = ["flight", "hotel", "restaurant", "shopping", "taxi", "other"];
const NOB_EXPENSE_NOTES = ["ארוחת בוקר", "טיסה הלוך", "מוניות לשדה", "קניות בשוק", "כניסה למוזיאון", "תחבורה ציבורית"];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const rnd = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const fmtDate = (d) => d.toISOString().split("T")[0];
const fmtTime = (h, m) => `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

const generateNOBData = () => {
  const dest = pick(NOB_DESTINATIONS);
  const today = new Date();
  const startD = new Date(today); startD.setDate(today.getDate() + rnd(10, 60));
  const endD = new Date(startD); endD.setDate(startD.getDate() + rnd(4, 10));
  const nights = Math.round((endD - startD) / 86400000);
  const airport = pick(NOB_AIRPORTS);
  const hotel = pick(NOB_HOTELS);
  const depHour = rnd(6, 14), arrHour = depHour + rnd(2, 5);
  const retDepH = rnd(8, 16), retArrH = retDepH + rnd(2, 4);
  const id9 = String(rnd(100000000, 999999999));
  const days = Array.from({ length: nights }, (_, i) => {
    const d = new Date(startD); d.setDate(startD.getDate() + i);
    const acts = pick(NOB_ACTIVITIES);
    return {
      id: Date.now() + Math.random() + i, date: fmtDate(d),
      dayTitle: `יום ${i + 1} — ${dest.name}`,
      activities: acts.map((title, j) => ({
        id: Date.now() + Math.random() + j, time: fmtTime(9 + j * 3, 0),
        title, description: `פעילות מומלצת ביום ${i + 1}`,
        location: "", mapsLink: "", notes: "", ticketUrl: "", ticketFile: "",
      })),
      accommodation: {
        hotelName: "", address: "", link: "", bookingLink: "", mapsLink: "",
        confirmationUrl: "", confirmationFile: "", checkoutTime: "11:00", bookingRating: ""
      },
    };
  });
  const expenses = Array.from({ length: rnd(3, 6) }, (_, i) => {
    const expD = new Date(startD); expD.setDate(startD.getDate() + rnd(0, nights - 1));
    return {
      id: `nob_exp_${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`,
      date: fmtDate(expD),
      category: pick(NOB_EXPENSE_CATS), customCategory: "",
      amount: String(rnd(20, 300)), currency: "LOCAL",
      note: pick(NOB_EXPENSE_NOTES), amountILS: null,
      source: "dashboard", perPerson: false
    };
  });
  const NOB_AIRLINES_MATCHED = [
    { label: "El Al / אל על", prefix: "LY" }, { label: "Wizz Air", prefix: "W6" },
    { label: "Ryanair", prefix: "FR" }, { label: "Lufthansa", prefix: "LH" },
    { label: "Turkish Airlines", prefix: "TK" }, { label: "easyJet", prefix: "U2" },
    { label: "KLM", prefix: "KL" },
  ];
  const airlineOut = pick(NOB_AIRLINES_MATCHED);
  const airlineIn = pick(NOB_AIRLINES_MATCHED);
  const NOB_CAR_COMPANIES = ["Hertz", "Avis", "Budget", "Sixt", "Europcar", "Enterprise"];
  const carCo = pick(NOB_CAR_COMPANIES);
  const NOB_TIPS_DATA = [
    { category: "food", text: `ב${dest.name} חייב לנסות את המנות המקומיות בשוק הטרי` },
    { category: "transport", text: "תחבורה ציבורית זולה ויעילה — כדאי לקנות כרטיס יומי" },
    { category: "money", text: 'עדיף למשוך מט"ח בכספומטים מקומיים ולא בשדה התעופה' },
    { category: "safety", text: "שמרו על הדרכון בכספת המלון ותצלמו אותו מראש" },
    { category: "culture", text: "לבוש צנוע נדרש באתרים דתיים" },
    { category: "general", text: `ממליצים להוריד מפות ${dest.name} offline לפני היציאה` },
  ];
  const randomTips = [...NOB_TIPS_DATA].sort(() => Math.random() - 0.5)
    .slice(0, rnd(3, 5)).map(t => ({ id: Date.now() + Math.random(), ...t }));
  const flightNum = `${airlineOut.prefix}${rnd(100, 9999)}`;
  const randomDocs = [
    { id: Date.now() + 1, category: "flight", files: [{ id: Date.now() + 2, label: `כרטיס טיסה ${flightNum}`, name: "flight_ticket.pdf", url: "" }] },
    { id: Date.now() + 3, category: "insurance", files: [{ id: Date.now() + 4, label: "פוליסת ביטוח נסיעות", name: "insurance.pdf", url: "" }] },
    { id: Date.now() + 5, category: "hotel", files: [{ id: Date.now() + 6, label: `אישור ${hotel}`, name: "hotel_confirmation.pdf", url: "" }] },
  ];
  const startDateStr = fmtDate(startD), endDateStr = fmtDate(endD);
  return {
    tripId: id9, tripTitle: `${dest.name} ${startD.getFullYear()}`,
    travelerName: pick(NOB_NAMES), travelerEmail: `test${rnd(10, 99)}@test.com`,
    travelerPhone: `05${rnd(0, 9)}${String(rnd(1000000, 9999999))}`,
    destination: dest.name, startDate: startDateStr, endDate: endDateStr,
    authCode: generateCode(), localCurrency: dest.currency, sharedHotel: true,
    sharedAccommodation: {
      hotelName: hotel, address: `${rnd(1, 200)} Main Street, ${dest.name}`,
      checkoutTime: "11:00", bookingRating: String(rnd(7, 10)), totalPrice: String(rnd(800, 4000)),
      bookingLink: "https://www.booking.com", mapsLink: "https://maps.google.com",
      link: "https://www.booking.com", freeCancellation: Math.random() > 0.5,
      amenities: "חניה חינם, מטבח, מרפסת עם נוף", confirmationUrl: "", confirmationFile: ""
    },
    flights: [
      {
        id: "outbound", departureAirport: "TLV", departureCity: "תל אביב",
        arrivalAirport: airport.iata, arrivalCity: airport.city,
        airline: airlineOut.label, airlineCustom: "", flightNumber: flightNum,
        departureTime: `${startDateStr}T${fmtTime(depHour, rnd(0, 59))}`,
        arrivalTime: `${startDateStr}T${fmtTime(arrHour, rnd(0, 59))}`
      },
      {
        id: "inbound", departureAirport: airport.iata, departureCity: airport.city,
        arrivalAirport: "TLV", arrivalCity: "תל אביב",
        airline: airlineIn.label, airlineCustom: "",
        flightNumber: `${airlineIn.prefix}${rnd(100, 9999)}`,
        departureTime: `${endDateStr}T${fmtTime(retDepH, rnd(0, 59))}`,
        arrivalTime: `${endDateStr}T${fmtTime(retArrH, rnd(0, 59))}`
      },
    ],
    itinerary: days, contacts: NOB_CONTACTS, expenses, documents: randomDocs, tips: randomTips,
    car: {
      company: carCo, carClass: pick(["CGMR", "IDAR", "SCAR", "JFAR", "ECAR"]),
      pickupLocation: `${airport.city} — שדה התעופה`,
      pickupMapsLink: "", pickupDayIdx: "0",
      returnLocation: `${airport.city} — שדה התעופה`,
      returnMapsLink: "", returnDayIdx: String(nights - 1),
      confirmationUrl: "", confirmationFile: ""
    },
  };
};

const EMPTY_ACTIVITY = () => ({
  id: Date.now() + Math.random(), type: "activity",
  time: "", title: "", description: "",
  location: "", mapsLink: "", notes: "", ticketUrl: "", ticketFile: "",
});

const EMPTY_TRAVEL = () => ({
  id: Date.now() + Math.random(), type: "travel",
  duration: "", note: "",
});

const buildDaysFromDates = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate), end = new Date(endDate), days = [];
  let current = new Date(start), idx = 1;
  while (current <= end) {
    days.push({
      id: Date.now() + Math.random() + idx,
      date: current.toISOString().split("T")[0], dayTitle: "",
      activities: [EMPTY_ACTIVITY()],
      weatherCity: "",
      accommodation: {
        hotelName: "", address: "", link: "", bookingLink: "", mapsLink: "",
        confirmationUrl: "", confirmationFile: "", checkoutTime: "", bookingRating: ""
      }
    });
    current.setDate(current.getDate() + 1); idx++;
  }
  return days;
};

const addIdsToData = (data) => ({
  ...data,
  itinerary: (data.itinerary || []).map(day => ({
    ...day, id: Date.now() + Math.random(),
   activities: (day.activities || []).map(act => ({ ...act, id: Date.now() + Math.random() })),
    weatherCity: day.weatherCity || "",
  })),
  documents: (data.documents || []).map(d => {
    if (d.files) return { ...d, id: d.id || Date.now() + Math.random() };
    return {
      id: Date.now() + Math.random(), category: d.category || "other",
      files: [{ id: Date.now() + Math.random(), label: d.name || "", name: d.name || "", url: d.url || "" }]
    };
  }),
  contacts: (data.contacts || []).map(c => ({ ...c, id: Date.now() + Math.random() })),
  expenses: (data.expenses || []).map(e => ({
    ...e,
    id: e.id || `exp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  })),
  tips: (data.tips || []).map(t => ({ ...t, id: t.id || Date.now() + Math.random() })),
});

const EMPTY_CONTACT = () => ({ id: Date.now() + Math.random(), name: "", role: "", phone: "" });
const waLink = (phone) => `https://wa.me/${phone.replace(/\D/g, "")}`;

// ─── Helpers ──────────────────────────────────────────────
const UploadProgressBar = ({ progress }) => (
  <div className="w-full mt-2">
    <div className="flex justify-between text-xs text-blue-300 mb-1">
      <span>מעלה קובץ...</span><span>{progress}%</span>
    </div>
    <div className="w-full bg-blue-900/60 rounded-full h-2">
      <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-2 rounded-full transition-all duration-300"
        style={{ width: `${progress}%` }} />
    </div>
  </div>
);

const uploadFileWithProgress = (storageRef, file, onProgress) =>
  new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed",
      snap => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
      <Icon size={20} className="text-blue-950" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-white tracking-wide">{title}</h2>
      {subtitle && <p className="text-blue-300 text-sm">{subtitle}</p>}
    </div>
  </div>
);

const inputCls = (readOnly) =>
  `bg-blue-900/40 border text-white placeholder-blue-500 rounded-xl px-4 py-3 transition-all duration-200 w-full
  ${readOnly ? "border-blue-800/30 opacity-70 cursor-default"
    : "border-blue-700/50 focus:outline-none focus:border-yellow-500/70 focus:ring-2 focus:ring-yellow-500/20"}`;

const selectCls = "bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-3 py-3 focus:outline-none focus:border-yellow-500/70 focus:ring-2 focus:ring-yellow-500/20";

const InputField = ({ label, readOnly, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-blue-200 text-sm font-medium">{label}</label>
    <input {...props} readOnly={readOnly} className={inputCls(readOnly)} />
  </div>
);

const TextareaField = ({ label, readOnly, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-blue-200 text-sm font-medium">{label}</label>
    <textarea {...props} readOnly={readOnly} rows={2} className={`${inputCls(readOnly)} resize-none`} />
  </div>
);

// ─── EuDateInput ─────────────────────────────────────────
const EuDateInput = ({ value, onChange, min, max, readOnly, className = "" }) => {
  const [raw, setRaw] = useState(() => value ? value.split("-").reverse().join("/") : "");
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { setRaw(value ? value.split("-").reverse().join("/") : ""); }, [value]);
  const toISO = (dd_mm_yyyy) => {
    const parts = dd_mm_yyyy.replace(/[^0-9]/g, "");
    if (parts.length < 8) return null;
    const d = parts.slice(0, 2), m = parts.slice(2, 4), y = parts.slice(4, 8);
    return `${y}-${m}-${d}`;
  };
  const handleChange = (e) => {
    let v = e.target.value.replace(/[^0-9/]/g, "");
    const digits = v.replace(/\//g, "");
    let formatted = digits;
    if (digits.length >= 2) formatted = digits.slice(0, 2) + "/" + digits.slice(2);
    if (digits.length >= 4) formatted = digits.slice(0, 2) + "/" + digits.slice(2, 4) + "/" + digits.slice(4, 8);
    setRaw(formatted);
    if (digits.length === 8) {
      const iso = toISO(formatted);
      if (!iso) { setErr("תאריך לא תקין"); return; }
      if (min && iso < min) { setErr(`לא לפני ${min.split("-").reverse().join("/")}`); onChange(""); return; }
      if (max && iso > max) { setErr(`לא אחרי ${max.split("-").reverse().join("/")}`); onChange(""); return; }
      setErr(""); onChange(iso);
    } else { setErr(""); if (digits.length === 0) onChange(""); }
  };
  const handleNativePick = (e) => {
    const iso = e.target.value;
    if (!iso) { onChange(""); setRaw(""); return; }
    if (min && iso < min) return;
    if (max && iso > max) return;
    setRaw(iso.split("-").reverse().join("/")); setErr(""); onChange(iso);
  };
  if (readOnly) return <div className={`${inputCls(true)} ${className}`} dir="ltr">{raw || "—"}</div>;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="relative flex items-center">
        <input ref={inputRef} value={raw} onChange={handleChange}
          placeholder="DD/MM/YYYY" maxLength={10} dir="ltr"
          className={`${inputCls(false)} ${className} ${err ? "border-red-500/70" : ""} pl-9`} />
        <label className="absolute right-2.5 cursor-pointer text-blue-400 hover:text-yellow-400 transition-colors z-10">
          <Calendar size={15} />
          <input type="date" value={value || ""} min={min || ""} max={max || ""}
            onChange={handleNativePick} className="absolute opacity-0 w-0 h-0 overflow-hidden" />
        </label>
      </div>
      {err && <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle size={11} />{err}</p>}
    </div>
  );
};

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

const TimeSelect = ({ value, onChange, readOnly }) => {
  const hh = value ? value.split(":")[0] : "";
  const mm = value ? value.split(":")[1] : "";
  if (readOnly) return <div className={inputCls(true)}>{value || "—"}</div>;
  return (
    <div className="flex gap-1.5">
      <select value={hh} onChange={e => onChange(e.target.value ? `${e.target.value}:${mm || "00"}` : "")}
        className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20
          focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
        <option value="">שע׳</option>
        {HOURS.map(h => <option key={h} value={h} className="bg-blue-950">{h}</option>)}
      </select>
      <span className="text-blue-400 self-center text-lg font-bold">:</span>
      <select value={mm} onChange={e => onChange(hh ? `${hh}:${e.target.value}` : "")}
        className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20
          focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
        <option value="">דק׳</option>
        {MINUTES.map(m => <option key={m} value={m} className="bg-blue-950">{m}</option>)}
      </select>
    </div>
  );
};

const DateTimeField = ({ label, value, onChange, readOnly, minDate, maxDate, allowNextDay }) => {
  const datePart = value ? value.split("T")[0] : "";
  const timePart = value ? (value.split("T")[1] || "").slice(0, 5) : "";
  const hh = timePart ? timePart.split(":")[0] : "";
  const mm = timePart ? timePart.split(":")[1] : "";
  const [dateErr, setDateErr] = useState("");
  const build = (d, h, m) => { if (!d) return ""; return `${d}T${h || "00"}:${m || "00"}`; };
  const effectiveMax = allowNextDay && maxDate
    ? (() => { const d = new Date(maxDate); d.setDate(d.getDate() + 1); return d.toISOString().split("T")[0]; })()
    : maxDate;
  const onDateChange = (d) => {
    let err = "";
    if (d && minDate && d < minDate) err = `לא לפני ${minDate.split("-").reverse().join("/")}`;
    else if (d && effectiveMax && d > effectiveMax) err = `לא אחרי ${effectiveMax.split("-").reverse().join("/")}`;
    setDateErr(err); onChange(build(d, hh, mm));
  };
  const displayDate = datePart ? datePart.split("-").reverse().join("/") : "";
  const displayValue = value ? `${displayDate}  ${timePart}` : "—";
  if (readOnly) return (
    <div className="flex flex-col gap-1">
      <label className="text-blue-200 text-sm font-medium">{label}</label>
      <div className={`${inputCls(true)} tracking-wide`} dir="ltr">{displayValue}</div>
    </div>
  );
  return (
    <div className="flex flex-col gap-1">
      <label className="text-blue-200 text-sm font-medium">{label}</label>
      <div className="flex gap-2" dir="ltr">
        <EuDateInput value={datePart} min={minDate || ""} max={effectiveMax || ""}
          onChange={onDateChange} className="flex-[2]" />
        <select value={hh} onChange={e => onChange(build(datePart, e.target.value, mm || "00"))}
          className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-16
            focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
          <option value="">HH</option>
          {HOURS.map(h => <option key={h} value={h} className="bg-blue-950">{h}</option>)}
        </select>
        <select value={mm} onChange={e => onChange(build(datePart, hh || "00", e.target.value))}
          className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-16
            focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
          <option value="">MM</option>
          {MINUTES.map(m => <option key={m} value={m} className="bg-blue-950">{m}</option>)}
        </select>
      </div>
      {allowNextDay && <p className="text-blue-500 text-xs">✈️ ניתן לבחור יום אחרי תאריך החזרה (טיסת לילה)</p>}
      {dateErr && <p className="text-red-400 text-xs flex items-center gap-1 mt-0.5"><AlertCircle size={12} />{dateErr}</p>}
    </div>
  );
};

const AirlineSelect = ({ value, customValue, onChangeAirline, onChangeCustom, readOnly }) => {
  const isOther = value === "אחר";
  if (readOnly) return (
    <div className="flex flex-col gap-1">
      <label className="text-blue-200 text-sm font-medium">חברת תעופה</label>
      <div className={inputCls(true)}>{isOther ? customValue : value}</div>
    </div>
  );
  return (
    <div className="flex flex-col gap-1">
      <label className="text-blue-200 text-sm font-medium">חברת תעופה</label>
      <select value={value} onChange={e => onChangeAirline(e.target.value)} className={inputCls(false)}>
        <option value="">בחר חברת תעופה...</option>
        {COMMON_AIRLINES.map(a => <option key={a} value={a} className="bg-blue-950">{a}</option>)}
      </select>
      {isOther && <input value={customValue} onChange={e => onChangeCustom(e.target.value)}
        placeholder="הזן שם חברת תעופה..." className={inputCls(false)} autoFocus />}
    </div>
  );
};

const AirportSearch = ({ value, onChange, label, readOnly }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value || "");
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef(null), listRef = useRef(null);
  useEffect(() => { setSelected(value || ""); }, [value]);
  const findAirport = code => COMMON_AIRPORTS.find(a => a.iata === code);
  const displayLabel = selected ? (findAirport(selected) ? `${selected} – ${findAirport(selected).city}` : selected) : "";
  const search = (q) => {
    setQuery(q); setHighlighted(-1);
    if (!q) { setResults([]); return; }
    const lower = q.toLowerCase();
    setResults(COMMON_AIRPORTS.filter(a =>
      a.iata.toLowerCase().includes(lower) || a.city.toLowerCase().includes(lower) || a.name.toLowerCase().includes(lower)
    ).slice(0, 8));
  };
  const select = (airport) => { setSelected(airport.iata); onChange(airport.iata, airport.city); setQuery(""); setResults([]); setOpen(false); setHighlighted(-1); };
  const handleKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (highlighted >= 0 && results[highlighted]) select(results[highlighted]); }
    else if (e.key === "Escape") { setOpen(false); setHighlighted(-1); }
  };
  useEffect(() => {
    if (highlighted >= 0 && listRef.current) {
      const item = listRef.current.children[highlighted];
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }, [highlighted]);
  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  if (readOnly) return (
    <div className="flex flex-col gap-1">
      <label className="text-blue-200 text-sm font-medium">{label}</label>
      <div className={`${inputCls(true)} flex items-center gap-2`}>
        <span className="text-yellow-400 font-bold text-sm">{selected}</span>
        {selected && <span className="text-blue-300">–</span>}
        <span className="text-white text-sm">{findAirport(selected)?.city || ""}</span>
      </div>
    </div>
  );
  return (
    <div className="flex flex-col gap-1 relative" ref={containerRef}>
      <label className="text-blue-200 text-sm font-medium">{label}</label>
      <input value={open ? query : displayLabel}
        onFocus={() => { setOpen(true); setQuery(""); setResults([]); setHighlighted(-1); }}
        onChange={e => search(e.target.value)} onKeyDown={handleKeyDown}
        placeholder="חפש שדה תעופה..." className={inputCls(false)} />
      {open && results.length > 0 && (
        <div ref={listRef} className="absolute z-50 w-full top-full mt-1 bg-blue-950 border border-blue-700/60 rounded-xl shadow-2xl overflow-hidden max-h-64 overflow-y-auto">
          {results.map((a, idx) => (
            <div key={a.iata} onMouseDown={() => select(a)}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
                ${idx === highlighted ? "bg-[#1B3F8B] border-r-2 border-[#D4A017]" : "hover:bg-blue-800/60"}`}>
              <span className="text-yellow-400 font-bold text-sm w-10 shrink-0">{a.iata}</span>
              <div>
                <div className="text-white text-sm font-medium">{a.city}</div>
                <div className="text-blue-400 text-xs">{a.name}</div>
              </div>
              {idx === highlighted && <span className="mr-auto text-[#D4A017] text-xs">↵</span>}
            </div>
          ))}
        </div>
      )}
      {open && query.length > 0 && results.length === 0 && (
        <div className="absolute z-50 w-full top-full mt-1 bg-blue-950 border border-blue-700/60 rounded-xl p-3">
          <input value={query} onChange={e => { setQuery(e.target.value); onChange(e.target.value, ""); }}
            placeholder="הזן קוד IATA ידנית (למשל: NRT)"
            className="w-full bg-transparent text-white text-sm focus:outline-none" autoFocus />
          <p className="text-blue-500 text-xs mt-1">לא נמצא — הזן ידנית</p>
        </div>
      )}
    </div>
  );
};

const FlightLegForm = ({ leg, onUpdate, label, badgeColor, readOnly, tripStart, tripEnd, isReturn, onAirportSelect }) => {
  const depDate = leg.departureTime ? leg.departureTime.split("T")[0] : "";
  const depTime = leg.departureTime ? leg.departureTime.split("T")[1]?.slice(0, 5) : "";
  const handleDeparture = (v) => { onUpdate("departureTime", v); if (!leg.arrivalTime && v) onUpdate("arrivalTime", v); };
  const handleArrival = (v) => {
    if (!v) { onUpdate("arrivalTime", v); return; }
    const aDate = v.split("T")[0], aTime = v.split("T")[1]?.slice(0, 5) || "";
    if (depDate && aDate === depDate && depTime && aTime && aTime < depTime) { onUpdate("arrivalTime", `${aDate}T${depTime}`); return; }
    onUpdate("arrivalTime", v);
  };
  return (
    <div className="bg-blue-900/30 border border-blue-700/40 rounded-xl p-4">
      <div className="mb-3"><span className={`text-xs font-bold px-2 py-1 rounded-lg border ${badgeColor}`}>{label}</span></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <AirportSearch label="שדה תעופה מוצא" value={leg.departureAirport} readOnly={readOnly}
          onChange={(code, city) => { onUpdate("departureAirport", code); onUpdate("departureCity", city); if (onAirportSelect) onAirportSelect("dep", code, city); }} />
        <AirportSearch label="שדה תעופה יעד" value={leg.arrivalAirport} readOnly={readOnly}
          onChange={(code, city) => { onUpdate("arrivalAirport", code); onUpdate("arrivalCity", city); if (onAirportSelect) onAirportSelect("arr", code, city); }} />
        <AirlineSelect value={leg.airline} customValue={leg.airlineCustom || ""}
          onChangeAirline={v => onUpdate("airline", v)} onChangeCustom={v => onUpdate("airlineCustom", v)} readOnly={readOnly} />
        <InputField label="מספר טיסה" placeholder="LY315" value={leg.flightNumber} readOnly={readOnly}
          onChange={e => onUpdate("flightNumber", e.target.value)} />
        <DateTimeField label="שעת המראה" value={leg.departureTime} readOnly={readOnly}
          minDate={tripStart} maxDate={tripEnd} onChange={handleDeparture} />
        <DateTimeField label="שעת נחיתה" value={leg.arrivalTime} readOnly={readOnly}
          minDate={depDate || tripStart} maxDate={tripEnd} allowNextDay={!!isReturn} onChange={handleArrival} />
      </div>
    </div>
  );
};

const FlightSection = ({ flights, setFlights, readOnly, tripStart, tripEnd }) => {
  const [multiDest, setMultiDest] = useState(flights.length > 2);
  const updateLeg = (id, field, value) => setFlights(prev => prev.map(f => f.id === id ? { ...f, [field]: value } : f));
  const addLeg = () => setFlights(prev => [...prev, EMPTY_FLIGHT_LEG()]);
  const removeLeg = (id) => setFlights(prev => prev.filter(f => f.id !== id));
  const handleOutboundAirport = (type, code, city) => {
    const returnLeg = flights[1];
    if (!returnLeg) return;
    if (type === "dep" && !returnLeg.arrivalAirport)
      setFlights(prev => prev.map((f, i) => i === 1 ? { ...f, arrivalAirport: code, arrivalCity: city } : f));
    else if (type === "arr" && !returnLeg.departureAirport)
      setFlights(prev => prev.map((f, i) => i === 1 ? { ...f, departureAirport: code, departureCity: city } : f));
  };
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-6">
        <SectionTitle icon={({ size, className }) => <span className={className} style={{ fontSize: size }}>✈️</span>}
          title="פרטי טיסה" subtitle="פרטי הטיסה הלוך וחזור" />
        {!readOnly && (
          <button type="button" onClick={() => {
            if (!multiDest) { setMultiDest(true); if (flights.length < 3) addLeg(); }
            else { setMultiDest(false); setFlights(prev => prev.slice(0, 2)); }
          }} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all border
              ${multiDest ? "bg-yellow-500/20 border-yellow-500/40 text-yellow-400"
              : "bg-blue-800/40 border-blue-700/40 text-blue-400 hover:border-yellow-500/40 hover:text-yellow-400"}`}>
            🌍 {multiDest ? "מצב רב-יעדים" : "יעדים מרובים"}
          </button>
        )}
      </div>
      {!multiDest ? (
        <div className="space-y-4">
          {flights[0] && <FlightLegForm leg={flights[0]} readOnly={readOnly}
            label="✈️ הלוך" badgeColor="bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
            tripStart={tripStart} tripEnd={tripEnd} onAirportSelect={handleOutboundAirport}
            onUpdate={(f, v) => updateLeg(flights[0].id, f, v)} />}
          {flights[1] && <FlightLegForm leg={flights[1]} readOnly={readOnly}
            label="🔄 חזור" badgeColor="bg-blue-500/10 border-blue-500/20 text-blue-400"
            tripStart={tripStart} tripEnd={tripEnd} isReturn={true}
            onUpdate={(f, v) => updateLeg(flights[1].id, f, v)} />}
        </div>
      ) : (
        <div className="space-y-4">
          {flights.map((leg, idx) => (
            <div key={leg.id} className="relative">
              {!readOnly && flights.length > 2 && (
                <button type="button" onClick={() => removeLeg(leg.id)}
                  className="absolute top-3 left-3 z-10 p-1 rounded hover:bg-red-500/20 text-blue-500 hover:text-red-400 transition-colors">
                  <Trash2 size={14} />
                </button>
              )}
              <FlightLegForm leg={leg} readOnly={readOnly}
                label={`טיסה ${idx + 1}`} badgeColor="bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
                tripStart={tripStart} tripEnd={tripEnd} onUpdate={(f, v) => updateLeg(leg.id, f, v)} />
            </div>
          ))}
          {!readOnly && (
            <button type="button" onClick={addLeg}
              className="w-full py-3 rounded-xl border-2 border-dashed border-blue-600 text-blue-400
                hover:border-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/5 transition-all duration-200
                flex items-center justify-center gap-2 font-medium text-sm">
              <Plus size={16} /> הוסף טיסה
            </button>
          )}
        </div>
      )}
    </section>
  );
};

const validateRating = (val) => {
  if (!val) return "";
  if (!/^\d+(\.\d?)?$/.test(val)) return "פורמט לא תקין (למשל: 8.9)";
  const num = parseFloat(val);
  if (num < 1 || num > 10) return "דירוג בין 1 ל-10";
  return "";
};

const AccommodationSection = ({ tripId, accommodation, onChange, readOnly, hotelIndex, setDocuments }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sizeError, setSizeError] = useState("");
  const [ratingErr, setRatingErr] = useState("");
  const acc = accommodation || {
    hotelName: "", address: "", link: "", bookingLink: "", mapsLink: "",
    confirmationUrl: "", confirmationFile: "", checkoutTime: "", bookingRating: ""
  };
  const update = (field, value) => !readOnly && onChange({ ...acc, [field]: value });
  const handleRating = (v) => {
    const clean = v.replace(/[^\d.]/g, "").replace(/\..*\./, "$&").replace(/^(\d*\.?\d?).*$/, "$1");
    update("bookingRating", clean); setRatingErr(validateRating(clean));
  };
  const checkoutHH = acc.checkoutTime ? acc.checkoutTime.split(":")[0] : "";
  const checkoutMM = acc.checkoutTime ? acc.checkoutTime.split(":")[1] : "";
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !tripId) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) { setSizeError(`מקסימום ${MAX_FILE_MB}MB`); e.target.value = ""; return; }
    setSizeError(""); setUploading(true); setProgress(0);
    try {
      const url = await uploadFileWithProgress(ref(storage, `trips/${tripId}/hotels/${Date.now()}_${file.name}`), file, setProgress);
      onChange({ ...acc, confirmationUrl: url, confirmationFile: file.name });
      // סנכרון אוטומטי לכספת מסמכים
      if (setDocuments) {
        setDocuments(prev => {
          const hotelName = acc.hotelName || (hotelIndex != null ? `מלון ${hotelIndex + 1}` : "מלון");
          const label = `אישור הזמנה — ${hotelName}`;
          const newFile = { id: Date.now() + Math.random(), label, name: file.name, url };
          // חפש קבוצת hotel קיימת
          const existingIdx = prev.findIndex(g => g.category === "hotel" && g.files?.some(f => f.label === label));
          if (existingIdx >= 0) {
            // עדכן קובץ קיים
            return prev.map((g, i) => i === existingIdx
              ? { ...g, files: g.files.map(f => f.label === label ? newFile : f) }
              : g);
          }
          // חפש קבוצת hotel כללית להוסיף אליה
          const hotelGroupIdx = prev.findIndex(g => g.category === "hotel");
          if (hotelGroupIdx >= 0) {
            return prev.map((g, i) => i === hotelGroupIdx
              ? { ...g, files: [...g.files, newFile] }
              : g);
          }
          // צור קבוצה חדשה
          return [...prev, { id: Date.now() + Math.random(), category: "hotel", files: [newFile] }];
        });
      }
    } catch (err) { console.error(err); }
    finally { setUploading(false); setProgress(0); e.target.value = ""; }
  };
  return (
    <div className="mt-4 bg-yellow-500/5 border border-yellow-500/25 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">🏨</span>
        <span className="text-yellow-400 font-bold text-sm">לינה</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InputField label="שם המלון" placeholder="Hilton Rome" value={acc.hotelName} readOnly={readOnly}
          onChange={e => update("hotelName", e.target.value)} />
        <div className="flex flex-col gap-1 justify-center">
          <label className="text-blue-200 text-sm font-medium">ביטול חינם</label>
          {readOnly
            ? <div className={inputCls(true)}>{acc.freeCancellation ? "✅ כן" : "❌ לא"}</div>
            : <div className="flex gap-3 mt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={`freeCancel_${acc.hotelName || "hotel"}`} checked={acc.freeCancellation === true}
                  onChange={() => update("freeCancellation", true)} className="accent-yellow-400 w-4 h-4" />
                <span className="text-green-400 text-sm font-semibold">✅ כן</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={`freeCancel_${acc.hotelName || "hotel"}`} checked={acc.freeCancellation === false}
                  onChange={() => update("freeCancellation", false)} className="accent-yellow-400 w-4 h-4" />
                <span className="text-red-400 text-sm font-semibold">❌ לא</span>
              </label>
            </div>
          }
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">דירוג Booking (1–10)</label>
          {readOnly
            ? <div className={inputCls(true)}>{acc.bookingRating || "—"}</div>
            : <div className="flex items-center gap-2">
              <input value={acc.bookingRating || ""} onChange={e => handleRating(e.target.value)}
                placeholder="8.9" inputMode="decimal" maxLength={4}
                className={`${inputCls(false)} w-28 ${ratingErr ? "border-red-500/70" : ""}`} />
              {acc.bookingRating && !ratingErr && (
                <div className="flex items-center gap-1 px-3 py-2 rounded-xl bg-blue-500/20 border border-blue-500/30">
                  <span className="text-blue-300 text-sm font-bold">{acc.bookingRating}</span>
                  <span className="text-yellow-400 text-sm">★</span>
                </div>
              )}
            </div>
          }
          {ratingErr && <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle size={12} />{ratingErr}</p>}
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">שעת Checkout</label>
          {readOnly
            ? <div className={inputCls(true)}>{acc.checkoutTime || "—"}</div>
            : <div className="flex gap-1.5 items-center" dir="ltr">
              <select value={checkoutHH} onChange={e => update("checkoutTime", e.target.value ? `${e.target.value}:${checkoutMM || "00"}` : "")}
                className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20 focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
                <option value="">שע׳</option>
                {HOURS.map(h => <option key={h} value={h} className="bg-blue-950">{h}</option>)}
              </select>
              <span className="text-blue-400 text-lg font-bold">:</span>
              <select value={checkoutMM} onChange={e => update("checkoutTime", checkoutHH ? `${checkoutHH}:${e.target.value}` : "")}
                className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20 focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
                <option value="">דק׳</option>
                {MINUTES.map(m => <option key={m} value={m} className="bg-blue-950">{m}</option>)}
              </select>
            </div>
          }
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">💰 מחיר כולל (₪)</label>
          {readOnly
            ? <div className={inputCls(true)}>{acc.totalPrice ? `₪ ${acc.totalPrice}` : "—"}</div>
            : <input type="number" min="0" placeholder="0" value={acc.totalPrice || ""} readOnly={readOnly}
              onChange={e => update("totalPrice", e.target.value)} className={inputCls(false)} dir="ltr" />
          }
        </div>
        <div className="md:col-span-2 flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">פרטים נוספים</label>
          <div className="text-blue-500 text-xs mb-1">למשל: חניה חינם, מטבח, מרפסת, ג'קוזי, מרחק מהמרכז...</div>
          {readOnly
            ? <div className={`${inputCls(true)} whitespace-pre-line`}>{acc.amenities || "—"}</div>
            : <textarea value={acc.amenities || ""} onChange={e => update("amenities", e.target.value)}
              rows={2} placeholder="חניה חינם, מטבח, מרפסת..." className={`${inputCls(false)} resize-none`} />
          }
        </div>
        <div className="md:col-span-2 flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">🏨 לינק ישיר ל-Booking.com</label>
          <div className="flex gap-2">
            <input placeholder="https://www.booking.com/hotel/..." value={acc.bookingLink || ""} readOnly={readOnly}
              onChange={e => update("bookingLink", e.target.value)} className={inputCls(readOnly)} />
            {acc.bookingLink && <a href={acc.bookingLink} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 px-4 py-3 rounded-xl bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-colors text-sm whitespace-nowrap">פתח</a>}
          </div>
        </div>
        <div className="md:col-span-2 flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">לינק (הזמנה / Google Maps)</label>
          <div className="flex gap-2">
            <input placeholder="https://..." value={acc.link || ""} readOnly={readOnly}
              onChange={e => update("link", e.target.value)} className={inputCls(readOnly)} />
            {acc.link && <a href={acc.link} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 px-4 py-3 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/40 transition-colors text-sm whitespace-nowrap">
              <MapPin size={14} /> פתח</a>}
          </div>
        </div>
        <div className="md:col-span-2 flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">אישור הזמנה (PDF)</label>
          {acc.confirmationUrl
            ? <div className="flex items-center gap-3 bg-blue-900/30 border border-blue-700/30 rounded-xl px-4 py-3">
              <FileText size={16} className="text-yellow-400 shrink-0" />
              <span className="text-white text-sm flex-1 truncate">{acc.confirmationFile}</span>
              <a href={acc.confirmationUrl} target="_blank" rel="noopener noreferrer"
                className="text-blue-400 hover:text-yellow-400 text-xs underline">פתח</a>
              {!readOnly && <button type="button" onClick={() => onChange({ ...acc, confirmationUrl: "", confirmationFile: "" })}
                className="text-blue-500 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>}
            </div>
            : !readOnly && <div>
              <label className={`flex items-center gap-2 px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 w-fit
                  ${uploading ? "bg-blue-700 text-blue-300 cursor-not-allowed" : "bg-blue-900/40 border border-blue-700/50 text-blue-300 hover:border-yellow-500/50 hover:text-yellow-400"}`}>
                <Upload size={15} />
                {uploading ? `מעלה... ${progress}%` : "העלה אישור הזמנה"}
                <input type="file" accept=".pdf,image/*" onChange={handleUpload} disabled={uploading || !tripId} className="hidden" />
              </label>
              {uploading && <UploadProgressBar progress={progress} />}
              {sizeError && <p className="text-red-400 text-xs mt-1">{sizeError}</p>}
              {!tripId && <p className="text-yellow-500 text-xs mt-1">יש להזין מספר זהות לפני העלאת קבצים</p>}
            </div>
          }
        </div>
      </div>
    </div>
  );
};


// ─── כרטיס נסיעה בין אתרים ───────────────────────────
const TravelCard = ({ item, onUpdate, onRemove, readOnly }) => {
  if (readOnly && !item.duration) return null;
  return (
    <div className="flex items-center gap-3 mx-4 my-1">
      {/* קו מחבר */}
      <div className="flex flex-col items-center gap-0 shrink-0">
        <div className="w-0.5 h-3 bg-blue-700/40 rounded" />
        <div className="w-8 h-8 rounded-full bg-blue-900/60 border border-blue-700/40 flex items-center justify-center">
          <span className="text-sm">🚗</span>
        </div>
        <div className="w-0.5 h-3 bg-blue-700/40 rounded" />
      </div>
      {/* תוכן */}
      <div className="flex-1 flex items-center gap-2 bg-blue-900/20 border border-blue-700/20 rounded-xl px-3 py-2">
        <span className="text-blue-400 text-xs font-medium whitespace-nowrap">נסיעה משוערת:</span>
        {readOnly
          ? <span className="text-white text-sm font-semibold">{item.duration} דקות</span>
          : <>
            <input type="number" min="1" max="600" placeholder="דקות"
              value={item.duration}
              onChange={e => onUpdate("duration", e.target.value.replace(/\D/g, "").slice(0, 3))}
              className="bg-transparent text-white text-sm font-semibold w-16 focus:outline-none border-b border-blue-600 focus:border-yellow-500/70 text-center"
              dir="ltr" />
            <span className="text-blue-400 text-xs">דק׳</span>
            {item.duration && (
              <span className="text-blue-500 text-xs">
                {parseInt(item.duration) >= 60
                  ? `(${Math.floor(parseInt(item.duration) / 60)}:${String(parseInt(item.duration) % 60).padStart(2, "0")} שע׳)`
                  : ""}
              </span>
            )}
            <input type="text" placeholder="הערה (אופציונלי)"
              value={item.note || ""}
              onChange={e => onUpdate("note", e.target.value)}
              className="flex-1 bg-transparent text-blue-400 text-xs focus:outline-none border-b border-blue-700/30 focus:border-yellow-500/50 min-w-0"
            />
          </>
        }
      </div>
      {!readOnly && (
        <button type="button" onClick={onRemove}
          className="p-1 rounded hover:bg-red-500/20 text-blue-600 hover:text-red-400 transition-colors shrink-0">
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
};

const ActivityCard = ({ tripId, activity, actIdx, onUpdate, onRemove, canRemove, readOnly }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const handleTicketUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !tripId) return;
    setUploading(true); setProgress(0);
    try {
      const url = await uploadFileWithProgress(ref(storage, `trips/${tripId}/tickets/${Date.now()}_${file.name}`), file, setProgress);
      onUpdate("ticketUrl", url); onUpdate("ticketFile", file.name);
    } catch (err) { console.error(err); }
    finally { setUploading(false); setProgress(0); e.target.value = ""; }
  };
  return (
    <div className="bg-blue-950/50 border border-blue-700/30 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-blue-300 text-xs font-semibold">פעילות {actIdx + 1}</span>
        {canRemove && !readOnly && (
          <button type="button" onClick={onRemove}
            className="p-1 rounded hover:bg-red-500/20 text-blue-500 hover:text-red-400 transition-colors">
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">שעה</label>
          {readOnly
            ? <div className={inputCls(true)} dir="ltr">{activity.time || "—"}</div>
            : <div className="flex gap-1.5 items-center" dir="ltr">
              <select value={activity.time ? activity.time.split(":")[0] : ""}
                onChange={e => {
                  const hh = e.target.value;
                  const mm = activity.time ? activity.time.split(":")[1] || "00" : "00";
                  onUpdate("time", hh ? `${hh}:${mm}` : "");
                }}
                className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20 focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
                <option value="">שע׳</option>
                {HOURS.map(h => <option key={h} value={h} className="bg-blue-950">{h}</option>)}
              </select>
              <span className="text-blue-400 font-bold text-lg">:</span>
              <select value={activity.time ? activity.time.split(":")[1] || "" : ""}
                onChange={e => {
                  const mm = e.target.value;
                  const hh = activity.time ? activity.time.split(":")[0] || "00" : "00";
                  onUpdate("time", hh ? `${hh}:${mm}` : "");
                }}
                className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20 focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
                <option value="">דק׳</option>
                {MINUTES.map(m => <option key={m} value={m} className="bg-blue-950">{m}</option>)}
              </select>
            </div>
          }
        </div>
        <InputField label="כותרת פעילות" placeholder="ביקור במוזיאון" value={activity.title} readOnly={readOnly}
          onChange={e => onUpdate("title", e.target.value)} />
        <div className="md:col-span-2">
          <TextareaField label="תיאור" placeholder="פרטים..." value={activity.description} readOnly={readOnly}
            onChange={e => onUpdate("description", e.target.value)} />
        </div>
        <div className="md:col-span-2 flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">לינק (Google Maps / אתר)</label>
          <div className="flex gap-2">
            <input placeholder="https://maps.google.com/..." value={activity.mapsLink || ""} readOnly={readOnly}
              onChange={e => { onUpdate("mapsLink", e.target.value); onUpdate("location", e.target.value); }} className={inputCls(readOnly)} />
            {activity.mapsLink && <a href={activity.mapsLink} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 px-4 py-3 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/40 transition-colors text-sm whitespace-nowrap">
              <MapPin size={14} /> פתח</a>}
          </div>
        </div>
        <div className="md:col-span-2">
          <TextareaField label="הערות" placeholder="הערות..." value={activity.notes || ""} readOnly={readOnly}
            onChange={e => onUpdate("notes", e.target.value)} />
        </div>
        <div className="md:col-span-2 flex flex-col gap-1">
          <label className="text-blue-200 text-sm font-medium">🎟️ כרטיס כניסה</label>
          {activity.ticketUrl
            ? <div className="flex items-center gap-2">
              <a href={activity.ticketUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 px-4 py-2 rounded-xl bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/25 transition-colors text-sm">
                <FileText size={14} /> פתח כרטיס
              </a>
              {!readOnly && <button type="button" onClick={() => { onUpdate("ticketUrl", ""); onUpdate("ticketFile", ""); }}
                className="p-2 rounded-xl hover:bg-red-500/20 text-blue-500 hover:text-red-400 transition-colors">
                <Trash2 size={14} /></button>}
              <span className="text-blue-400 text-xs truncate">{activity.ticketFile}</span>
            </div>
            : !readOnly && <label className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm cursor-pointer transition-all w-fit
                ${uploading ? "bg-blue-700/50 text-blue-400 cursor-not-allowed"
                : "border border-dashed border-blue-600 text-blue-400 hover:border-yellow-500 hover:text-yellow-400"}`}>
              <Upload size={14} /> {uploading ? `מעלה... ${progress}%` : "העלה כרטיס"}
              <input type="file" accept=".pdf,image/*" onChange={handleTicketUpload} disabled={uploading || !tripId} className="hidden" />
            </label>
          }
          {uploading && <UploadProgressBar progress={progress} />}
        </div>
      </div>
    </div>
  );
};

const ItinerarySection = ({ days, setDays, tripId, readOnly, sharedHotel, setSharedHotel, sharedAccommodation, setSharedAccommodation, setDocuments }) => {
  const [openDayId, setOpenDayId] = useState(days[0]?.id ?? -1);
  const [hotelMode, setHotelMode] = useState("single");
  const [openActivityId, setOpenActivityId] = useState(null);
  const dayRefs = useRef({});
  const toggle = (id) => {
    setOpenActivityId(null);
    setOpenDayId(prev => {
      const next = prev === id ? -1 : id;
      if (next !== -1) setTimeout(() => { const el = dayRefs.current[next]; if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 50);
      return next;
    });
  };
  const updateDay = (id, field, value) => setDays(d => d.map(day => day.id === id ? { ...day, [field]: value } : day));
  const addActivity = (dayId) => setDays(d => d.map(day => day.id === dayId ? { ...day, activities: [...day.activities, EMPTY_ACTIVITY()] } : day));
  const addTravel = (dayId) => setDays(d => d.map(day => day.id === dayId ? { ...day, activities: [...day.activities, EMPTY_TRAVEL()] } : day));
  const removeActivity = (dayId, actId) => setDays(d => d.map(day => day.id === dayId ? { ...day, activities: day.activities.filter(a => a.id !== actId) } : day));
  const updateActivity = (dayId, actId, field, value) => setDays(d => d.map(day =>
    day.id === dayId ? { ...day, activities: day.activities.map(a => a.id === actId ? { ...a, [field]: value } : a) } : day));
  const handleHotelModeChange = (mode) => { setHotelMode(mode); setSharedHotel(mode === "single"); };
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <SectionTitle icon={MapPin} title="בונה מסלול" subtitle="ימים, פעילויות ולינה" />
      </div>
      {!readOnly && days.length > 0 && (
        <div className="flex items-center gap-3 mb-5 p-3 bg-blue-900/30 rounded-xl border border-blue-700/30">
          <span className="text-blue-300 text-sm font-medium">🏨 מצב מלון:</span>
          <div className="flex gap-2">
            {["single", "perday"].map(mode => (
              <button key={mode} type="button" onClick={() => handleHotelModeChange(mode)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border
                  ${hotelMode === mode ? "bg-yellow-500/20 border-yellow-500/40 text-yellow-400"
                    : "bg-blue-800/40 border-blue-700/40 text-blue-400 hover:border-yellow-500/40"}`}>
                {mode === "single" ? "מלון אחד לכל השהות" : "מלון שונה לכל יום"}
              </button>
            ))}
          </div>
        </div>
      )}
      {hotelMode === "single" && days.length > 0 && (
        <div className="mb-5">
          <AccommodationSection tripId={tripId}
            accommodation={sharedAccommodation || { hotelName: "", address: "", link: "", bookingLink: "", mapsLink: "", confirmationUrl: "", confirmationFile: "", checkoutTime: "", bookingRating: "" }}
            readOnly={readOnly} hotelIndex={0} setDocuments={setDocuments}
            onChange={val => setSharedAccommodation(val)} />
        </div>
      )}
      {days.length === 0 && <p className="text-blue-500 text-center py-6 text-sm">יש לבחור תאריך יציאה וחזרה</p>}
      <div className="space-y-3">
        {days.map((day, idx) => {
          const isOpen = openDayId === day.id;
          return (
            <div key={day.id} ref={el => dayRefs.current[day.id] = el}
              className="bg-blue-900/30 border border-blue-700/40 rounded-xl overflow-hidden">
              <div className={`flex items-center justify-between px-5 py-4 cursor-pointer transition-colors
                  ${isOpen ? "bg-blue-800/40" : "hover:bg-blue-800/30"}`}
                onClick={() => toggle(day.id)}>
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold
                    ${isOpen ? "bg-gradient-to-br from-yellow-400 to-yellow-600 text-blue-950" : "bg-blue-800 text-blue-300"}`}>
                    {idx + 1}
                  </span>
                  <span className={`font-semibold ${isOpen ? "text-white" : "text-blue-300"}`}>{day.dayTitle || `יום ${idx + 1}`}</span>
                  <span className="text-yellow-400/70 text-xs bg-yellow-500/10 border border-yellow-500/20 px-2 py-0.5 rounded-lg">📅 {day.date}</span>
                  <span className="text-blue-500 text-xs">{day.activities.length} פעילויות</span>
                </div>
                {isOpen ? <ChevronUp size={18} className="text-yellow-400" /> : <ChevronDown size={18} className="text-blue-400" />}
              </div>
              {isOpen && (
                <div className="px-5 pb-5 border-t border-blue-700/30 pt-4 space-y-4">
                  <InputField label="כותרת היום" placeholder="יום ראשון ברומא"
                    value={day.dayTitle} readOnly={readOnly}
                    onChange={e => updateDay(day.id, "dayTitle", e.target.value)} />
                  <InputField label="עיר (למזג אוויר)" placeholder="Rome, Athens, Paris..."
                    value={day.weatherCity || ""}
                    readOnly={readOnly}
                    onChange={e => updateDay(day.id, "weatherCity", e.target.value)} />
                  <div className="space-y-3">
                    {day.activities.map((act, actIdx) => (
                      act.type === "travel"
                        ? <TravelCard key={act.id} item={act} readOnly={readOnly}
                          onUpdate={(field, value) => updateActivity(day.id, act.id, field, value)}
                          onRemove={() => removeActivity(day.id, act.id)} />
                        : (
                          <div key={act.id} className="bg-blue-950/50 border border-blue-700/30 rounded-xl overflow-hidden">
                            {/* כותרת לחיצה */}
                            <div
                              className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors
            ${openActivityId === act.id ? "bg-blue-800/40" : "hover:bg-blue-800/20"}`}
                              onClick={() => setOpenActivityId(prev => prev === act.id ? null : act.id)}>
                              <div className="flex items-center gap-2 min-w-0">
                                {act.time && (
                                  <span className="text-xs font-mono bg-blue-900/60 border border-blue-700/40 px-2 py-0.5 rounded-lg text-blue-300 shrink-0">
                                    {act.time}
                                  </span>
                                )}
                                <span className="text-white text-sm font-semibold truncate">
                                  {act.title || `פעילות ${actIdx + 1}`}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {day.activities.length > 1 && !readOnly && openActivityId !== act.id && (
                                  <button type="button"
                                    onClick={e => { e.stopPropagation(); removeActivity(day.id, act.id); }}
                                    className="p-1 rounded hover:bg-red-500/20 text-blue-500 hover:text-red-400 transition-colors">
                                    <Trash2 size={14} />
                                  </button>
                                )}
                                {openActivityId === act.id
                                  ? <ChevronUp size={16} className="text-yellow-400" />
                                  : <ChevronDown size={16} className="text-blue-400" />}
                              </div>
                            </div>
                            {/* תוכן — רק אם פתוח */}
                            {openActivityId === act.id && (
                              <div className="px-4 pb-4 pt-2 border-t border-blue-700/30">
                                <ActivityCard tripId={tripId} activity={act} actIdx={actIdx} readOnly={readOnly}
                                  canRemove={day.activities.length > 1}
                                  onUpdate={(field, value) => updateActivity(day.id, act.id, field, value)}
                                  onRemove={() => removeActivity(day.id, act.id)} />
                              </div>
                            )}
                          </div>
                        )
                    ))}
                  </div>
                  {!readOnly && (
                    <div className="flex gap-2">
                      <button type="button" onClick={() => addActivity(day.id)}
                        className="flex-1 py-2.5 rounded-xl border border-dashed border-blue-600 text-blue-400
                          hover:border-blue-400 hover:text-blue-300 hover:bg-blue-800/20 transition-all duration-200
                          flex items-center justify-center gap-2 text-sm font-medium">
                        <Plus size={15} /> הוסף פעילות
                      </button>
                      <button type="button" onClick={() => addTravel(day.id)}
                        className="flex-1 py-2.5 rounded-xl border border-dashed border-blue-700/50 text-blue-500
                          hover:border-blue-600 hover:text-blue-400 hover:bg-blue-900/20 transition-all duration-200
                          flex items-center justify-center gap-2 text-sm font-medium">
                        🚗 הוסף נסיעה
                      </button>
                    </div>
                  )}
                  {hotelMode === "perday" && (day.accommodation?.hotelName || !readOnly) && (
                    <>
                      {!day.accommodation?.hotelName && !readOnly && (
                        <button type="button"
                          onClick={() => updateDay(day.id, "accommodation", { ...day.accommodation, hotelName: " " })}
                          className="w-full py-2.5 rounded-xl border border-dashed border-yellow-600/50 text-yellow-600
                            hover:border-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/5 transition-all duration-200
                            flex items-center justify-center gap-2 text-sm font-medium">
                          🏨 הוסף מלון ליום זה
                        </button>
                      )}
                      {day.accommodation?.hotelName && (
                        <AccommodationSection tripId={tripId} accommodation={day.accommodation} readOnly={readOnly}
                          hotelIndex={idx}
                          setDocuments={setDocuments}
                          onChange={val => updateDay(day.id, "accommodation", val)} />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const DocGroup = ({ tripId, group, onChange, onRemoveGroup, readOnly }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sizeError, setSizeError] = useState("");
  const catInfo = DOC_CATEGORIES.find(c => c.value === group.category) || DOC_CATEGORIES[0];
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !tripId) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) { setSizeError(`מקסימום ${MAX_FILE_MB}MB`); e.target.value = ""; return; }
    setSizeError(""); setUploading(true); setProgress(0);
    try {
      const url = await uploadFileWithProgress(ref(storage, `trips/${tripId}/docs/${Date.now()}_${file.name}`), file, setProgress);
      onChange({ ...group, files: [...(group.files || []), { id: Date.now() + Math.random(), label: "", name: file.name, url }] });
    } catch (err) { console.error(err); }
    finally { setUploading(false); setProgress(0); e.target.value = ""; }
  };
  return (
    <div className="bg-blue-900/30 border border-blue-700/40 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{catInfo.emoji}</span>
          {readOnly
            ? <span className="text-white font-bold text-base">{catInfo.label}</span>
            : <select value={group.category} onChange={e => onChange({ ...group, category: e.target.value })}
              className="bg-blue-950/60 border border-blue-700/50 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-500/70">
              {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value} className="bg-blue-950">{c.emoji} {c.label}</option>)}
            </select>
          }
        </div>
        {!readOnly && <button type="button" onClick={onRemoveGroup}
          className="p-1.5 rounded-lg hover:bg-red-500/20 text-blue-500 hover:text-red-400 transition-colors">
          <Trash2 size={16} /></button>}
      </div>
      <div className="space-y-2 mb-3">
        {!(group.files || []).length
          ? <p className="text-blue-500 text-sm text-center py-2">טרם הועלו קבצים</p>
          : (group.files || []).map(f => (
            <div key={f.id} className="flex items-center gap-2 bg-blue-950/40 rounded-xl px-3 py-2">
              <FileText size={14} className="text-yellow-400 shrink-0" />
              {readOnly
                ? <span className="text-white text-sm flex-1 truncate">{f.label || f.name}</span>
                : <input value={f.label} placeholder={f.name}
                  onChange={e => onChange({ ...group, files: group.files.map(x => x.id === f.id ? { ...x, label: e.target.value } : x) })}
                  className="flex-1 bg-transparent text-white text-sm placeholder-blue-600 focus:outline-none border-b border-blue-700/50 focus:border-yellow-500/70 pb-0.5" />
              }
              <a href={f.url} target="_blank" rel="noopener noreferrer"
                className="text-blue-400 hover:text-yellow-400 text-xs shrink-0 transition-colors">פתח</a>
              {!readOnly && <button type="button" onClick={() => onChange({ ...group, files: group.files.filter(x => x.id !== f.id) })}
                className="p-1 rounded hover:bg-red-500/20 text-blue-600 hover:text-red-400 transition-colors shrink-0">
                <Trash2 size={12} /></button>}
            </div>
          ))
        }
      </div>
      {!readOnly && (
        <div className="flex flex-col gap-1">
          {!tripId
            ? <p className="text-yellow-400 text-xs flex items-center gap-1"><AlertCircle size={12} /> יש להזין מספר זהות תחילה</p>
            : <label className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition-all
                ${uploading ? "bg-blue-700/50 text-blue-400 cursor-not-allowed"
                : "border border-dashed border-blue-600 text-blue-400 hover:border-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/5"}`}>
              <Upload size={14} /> {uploading ? `מעלה... ${progress}%` : "הוסף קובץ"}
              <input type="file" accept=".pdf,image/*" onChange={handleUpload} disabled={uploading} className="hidden" />
            </label>
          }
          {uploading && <UploadProgressBar progress={progress} />}
          {sizeError && <p className="text-red-400 text-xs">{sizeError}</p>}
        </div>
      )}
    </div>
  );
};

const VaultSection = ({ tripId, documents, setDocuments, readOnly }) => {
  const addGroup = () => setDocuments(d => [...d, { id: Date.now(), category: "flight", files: [] }]);
  const updateGroup = (id, updated) => setDocuments(d => d.map(g => g.id === id ? updated : g));
  const removeGroup = (id) => setDocuments(d => d.filter(g => g.id !== id));
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <SectionTitle icon={FileText} title="כספת מסמכים" subtitle="כרטיסי טיסה, ביטוח, דרכונים ועוד" />
      <div className="space-y-3 mb-4">
        {!documents.length
          ? <p className="text-blue-500 text-center py-8 text-sm">טרם נוספו מסמכים</p>
          : documents.map(group => (
            <DocGroup key={group.id} tripId={tripId} group={group}
              onChange={updated => updateGroup(group.id, updated)}
              onRemoveGroup={() => removeGroup(group.id)} readOnly={readOnly} />
          ))
        }
      </div>
      {!readOnly && (
        <button type="button" onClick={addGroup}
          className="w-full py-3 rounded-xl border-2 border-dashed border-blue-600 text-blue-400
            hover:border-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/5 transition-all duration-200
            flex items-center justify-center gap-2 font-medium">
          <Plus size={18} /> הוסף סוג מסמך
        </button>
      )}
    </section>
  );
};

// ─── ACRISS מבנה 4 אותיות ────────────────────────────────
const ACRISS_CHAR1 = [
  { code: "M", label: "Mini" },
  { code: "E", label: "Economy" },
  { code: "C", label: "Compact" },
  { code: "I", label: "Intermediate" },
  { code: "S", label: "Standard" },
  { code: "F", label: "Full Size" },
  { code: "P", label: "Premium" },
  { code: "L", label: "Luxury" },
  { code: "X", label: "Special / Electric" },
  { code: "G", label: "Crossover" },
  { code: "U", label: "Unique" },
  { code: "K", label: "Commercial / Truck" },
];
const ACRISS_CHAR2 = [
  { code: "B", label: "2 דלתות" },
  { code: "C", label: "2-4 דלתות" },
  { code: "D", label: "4 דלתות (סדאן)" },
  { code: "W", label: "Estate / Station Wagon" },
  { code: "V", label: "מיניוואן" },
  { code: "G", label: "Crossover / Group" },
  { code: "L", label: "לימוזינה" },
  { code: "S", label: "ספורט" },
  { code: "T", label: "קבריולה" },
  { code: "F", label: "SUV" },
  { code: "J", label: "שטח 4×4" },
  { code: "X", label: "מיוחד" },
  { code: "P", label: "פיקאפ" },
  { code: "Q", label: "SUV נוסף" },
  { code: "R", label: "Recreational" },
  { code: "K", label: "Truck / Commercial" },
  { code: "H", label: "Motorhome" },
  { code: "E", label: "Estate (חלופי)" },
  { code: "N", label: "4WD SUV" },
];
const ACRISS_CHAR3 = [
  { code: "M", label: "ידני" },
  { code: "A", label: "אוטומט" },
  { code: "N", label: "ידני (לא מוגדר)" },
  { code: "C", label: "אוטומט (לא מוגדר)" },
];
const ACRISS_CHAR4 = [
  { code: "R", label: "מיזוג אוויר" },
  { code: "N", label: "ללא מיזוג" },
  { code: "D", label: "מיזוג 2WD" },
  { code: "Q", label: "מיזוג 4WD" },
];

const decodeAcriss = (code) => {
  if (!code || code.length !== 4) return null;
  return {
    c1: ACRISS_CHAR1.find(c => c.code === code[0]),
    c2: ACRISS_CHAR2.find(c => c.code === code[1]),
    c3: ACRISS_CHAR3.find(c => c.code === code[2]),
    c4: ACRISS_CHAR4.find(c => c.code === code[3]),
  };
};

const CarClassSearch = ({ value, onChange, readOnly }) => {
  const [manualCode, setManualCode] = useState(value || "");
  const [ch1, setCh1] = useState(value?.[0] || "");
  const [ch2, setCh2] = useState(value?.[1] || "");
  const [ch3, setCh3] = useState(value?.[2] || "");
  const [ch4, setCh4] = useState(value?.[3] || "");

  useEffect(() => {
    setManualCode(value || "");
    setCh1(value?.[0] || ""); setCh2(value?.[1] || "");
    setCh3(value?.[2] || ""); setCh4(value?.[3] || "");
  }, [value]);

  const applyCode = (raw) => {
    const upper = raw.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4);
    setManualCode(upper);
    setCh1(upper[0] || ""); setCh2(upper[1] || "");
    setCh3(upper[2] || ""); setCh4(upper[3] || "");
    onChange(upper);
  };

  const applySelects = (n1, n2, n3, n4) => {
    const code = `${n1}${n2}${n3}${n4}`;
    setManualCode(code); onChange(code);
  };

  const decoded = decodeAcriss(manualCode.length === 4 ? manualCode : "");

  if (readOnly) {
    const dec = decodeAcriss(value || "");
    return (
      <div className="space-y-2">
        <div className={`${inputCls(true)} flex items-center gap-2`}>
          <span className="text-yellow-400 font-black tracking-widest">{value || "—"}</span>
        </div>
        {dec && (
          <div className="flex flex-wrap gap-1.5">
            {[dec.c1, dec.c2, dec.c3, dec.c4].map((c, i) => c && (
              <span key={i} className="px-2 py-1 rounded-lg bg-blue-900/40 border border-blue-700/30 text-blue-300 text-xs">
                <span className="text-yellow-400 font-bold">{["גודל", "מרכב", "הילוך", "מיזוג"][i]}:</span>
                <span className="mr-1">{c.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* שדה טקסט חופשי */}
      <div className="flex gap-2 items-center">
        <input value={manualCode} onChange={e => applyCode(e.target.value)}
          placeholder="הקלד קוד ACRISS (CGMR, IDAR...)" maxLength={4} dir="ltr"
          className={`${inputCls(false)} font-mono text-yellow-400 font-black tracking-[0.3em] text-lg uppercase flex-1`} />
        {manualCode && (
          <button type="button" onClick={() => { applyCode(""); setCh1(""); setCh2(""); setCh3(""); setCh4(""); }}
            className="p-2 rounded-xl text-blue-500 hover:text-red-400 transition-colors">✕</button>
        )}
      </div>
      {/* 4 קומבו בוקסים */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { label: "גודל/סוג", options: ACRISS_CHAR1, val: ch1, set: v => { setCh1(v); applySelects(v, ch2, ch3, ch4); } },
          { label: "מרכב", options: ACRISS_CHAR2, val: ch2, set: v => { setCh2(v); applySelects(ch1, v, ch3, ch4); } },
          { label: "הילוכים", options: ACRISS_CHAR3, val: ch3, set: v => { setCh3(v); applySelects(ch1, ch2, v, ch4); } },
          { label: "מיזוג", options: ACRISS_CHAR4, val: ch4, set: v => { setCh4(v); applySelects(ch1, ch2, ch3, v); } },
        ].map((col, i) => (
          <div key={i} className="flex flex-col gap-1">
            <label className="text-blue-400 text-xs font-medium">{col.label}</label>
            <select value={col.val} onChange={e => col.set(e.target.value)}
              className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2 text-xs focus:outline-none focus:border-yellow-500/70 appearance-none">
              <option value="" className="bg-blue-950">—</option>
              {col.options.map(o => (
                <option key={o.code} value={o.code} className="bg-blue-950">{o.code} — {o.label}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
      {/* פענוח */}
      {manualCode.length === 4 && (
        <div className={`flex flex-wrap gap-1.5 p-2 rounded-xl border ${decoded ? "bg-blue-900/20 border-blue-700/30" : "bg-red-500/10 border-red-500/30"}`}>
          {decoded
            ? [decoded.c1, decoded.c2, decoded.c3, decoded.c4].map((c, i) => c && (
              <span key={i} className="px-2 py-1 rounded-lg bg-blue-800/40 text-xs">
                <span className="text-yellow-400 font-bold">{["גודל", "מרכב", "הילוך", "מיזוג"][i]}:</span>
                <span className="text-white mr-1">{c.label}</span>
              </span>
            ))
            : <span className="text-yellow-400 text-xs">קוד מותאם אישית — {manualCode}</span>
          }
        </div>
      )}
    </div>
  );
};

// ─── רכב שכור (UPDATED) ──────────────────────────────────
const EMPTY_CAR = () => ({
  company: "", carClass: "",
  pickupLocation: "", pickupMapsLink: "",
  pickupDayIdx: "", pickupTime: "",
  returnLocation: "", returnMapsLink: "",
  returnDayIdx: "", returnTime: "",
  sameReturnLocation: false,
  confirmationUrl: "", confirmationFile: "",
});

const CarSection = ({ tripId, car, setCar, readOnly, days }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sizeError, setSizeError] = useState("");
  const [enabled, setEnabled] = useState(!!(car?.company || car?.pickupLocation || car?.confirmationUrl));
  const update = (field, val) => setCar(prev => ({ ...(prev || EMPTY_CAR()), [field]: val }));
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !tripId) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) { setSizeError(`מקסימום ${MAX_FILE_MB}MB`); e.target.value = ""; return; }
    setSizeError(""); setUploading(true); setProgress(0);
    try {
      const url = await uploadFileWithProgress(ref(storage, `trips/${tripId}/car/${Date.now()}_${file.name}`), file, setProgress);
      update("confirmationUrl", url); update("confirmationFile", file.name);
    } catch (err) { console.error(err); }
    finally { setUploading(false); setProgress(0); e.target.value = ""; }
  };
  const c = car || EMPTY_CAR();
  const dayOptions = (days || []).map((day, idx) => ({
    idx,
    label: `יום ${idx + 1}${day.dayTitle ? ` — ${day.dayTitle}` : ""} (${day.date ? day.date.split("-").reverse().join("/") : ""})`,
  }));
  if (readOnly && !c.company && !c.pickupLocation && !c.confirmationUrl) return null;
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <SectionTitle icon={Car} title="רכב שכור" subtitle="פרטי השכרת רכב" />
        {!readOnly && (
          <button type="button" onClick={() => { setEnabled(e => !e); if (enabled) setCar(null); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all
              ${enabled ? "bg-red-500/15 border-red-500/40 text-red-400 hover:bg-red-500/25"
                : "bg-blue-800/40 border-blue-700/40 text-blue-400 hover:border-yellow-500/40 hover:text-yellow-400"}`}>
            {enabled ? "הסר רכב" : "+ הוסף רכב שכור"}
          </button>
        )}
      </div>
      {(enabled || readOnly) && (
        <div className="space-y-5">
          <InputField label="חברת השכרה" placeholder="Hertz, Avis, Budget..."
            value={c.company} readOnly={readOnly} onChange={e => update("company", e.target.value)} />
          <div className="flex flex-col gap-1">
            <label className="text-blue-200 text-sm font-medium">קלאס רכב (ACRISS)</label>
            <CarClassSearch value={c.carClass || ""} onChange={v => update("carClass", v)} readOnly={readOnly} />
          </div>
          {/* ══ בלוק איסוף ══ */}
          <div className="bg-blue-900/30 border border-blue-700/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-yellow-400 text-base">📍</span>
              <span className="text-yellow-400 font-bold text-sm">איסוף</span>
            </div>
            {dayOptions.length > 0 && (
              <div className="flex flex-col gap-1">
                <label className="text-blue-200 text-sm font-medium">יום מהמסלול</label>
                {readOnly
                  ? <div className={inputCls(true)}>{c.pickupDayIdx !== "" && c.pickupDayIdx !== undefined ? dayOptions[Number(c.pickupDayIdx)]?.label || "—" : "לא משויך"}</div>
                  : <select value={c.pickupDayIdx ?? ""} onChange={e => update("pickupDayIdx", e.target.value)} className={inputCls(false)}>
                    <option value="" className="bg-blue-950">— לא משויך —</option>
                    {dayOptions.map(d => <option key={d.idx} value={d.idx} className="bg-blue-950">{d.label}</option>)}
                  </select>
                }
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">מיקום איסוף</label>
              <input placeholder="שדה התעופה, כתובת..." value={c.pickupLocation} readOnly={readOnly}
                onChange={e => update("pickupLocation", e.target.value)} className={inputCls(readOnly)} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">🕐 שעת איסוף</label>
              {readOnly
                ? <div className={inputCls(true)} dir="ltr">{c.pickupTime || "—"}</div>
                : <div className="flex gap-1.5 items-center" dir="ltr">
                  <select value={c.pickupTime ? c.pickupTime.split(":")[0] : ""}
                    onChange={e => { const hh = e.target.value, mm = c.pickupTime ? c.pickupTime.split(":")[1] || "00" : "00"; update("pickupTime", hh ? `${hh}:${mm}` : ""); }}
                    className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20 focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
                    <option value="">שע׳</option>
                    {HOURS.map(h => <option key={h} value={h} className="bg-blue-950">{h}</option>)}
                  </select>
                  <span className="text-blue-400 font-bold text-lg">:</span>
                  <select value={c.pickupTime ? c.pickupTime.split(":")[1] || "" : ""}
                    onChange={e => { const mm = e.target.value, hh = c.pickupTime ? c.pickupTime.split(":")[0] || "00" : "00"; update("pickupTime", hh ? `${hh}:${mm}` : ""); }}
                    className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20 focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
                    <option value="">דק׳</option>
                    {MINUTES.map(m => <option key={m} value={m} className="bg-blue-950">{m}</option>)}
                  </select>
                </div>
              }
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">🗺️ לינק Google Maps — איסוף</label>
              <div className="flex gap-2">
                <input placeholder="https://maps.google.com/..." value={c.pickupMapsLink || ""} readOnly={readOnly}
                  onChange={e => update("pickupMapsLink", e.target.value)} className={inputCls(readOnly)} />
                {c.pickupMapsLink && <a href={c.pickupMapsLink} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/40 transition-colors text-sm whitespace-nowrap">
                  <MapPin size={14} /> פתח</a>}
              </div>
            </div>
            {/* אישור השכרה — בחלק האיסוף בלבד */}
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">📄 אישור השכרה</label>
              {c.confirmationUrl
                ? <div className="flex items-center gap-3 bg-blue-900/30 border border-blue-700/30 rounded-xl px-4 py-3">
                  <FileText size={16} className="text-yellow-400 shrink-0" />
                  <span className="text-white text-sm flex-1 truncate">{c.confirmationFile}</span>
                  <a href={c.confirmationUrl} target="_blank" rel="noopener noreferrer"
                    className="text-blue-400 hover:text-yellow-400 text-xs underline">פתח</a>
                  {!readOnly && <button type="button"
                    onClick={() => { update("confirmationUrl", ""); update("confirmationFile", ""); }}
                    className="text-blue-500 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>}
                </div>
                : !readOnly && (
                  <div>
                    <label className={`flex items-center gap-2 px-4 py-3 rounded-xl cursor-pointer transition-all w-fit
                      ${uploading ? "bg-blue-700 text-blue-300 cursor-not-allowed"
                        : "bg-blue-900/40 border border-blue-700/50 text-blue-300 hover:border-yellow-500/50 hover:text-yellow-400"}`}>
                      <Upload size={15} />
                      {uploading ? `מעלה... ${progress}%` : "העלה אישור השכרה"}
                      <input type="file" accept=".pdf,image/*" onChange={handleUpload} disabled={uploading || !tripId} className="hidden" />
                    </label>
                    {uploading && <UploadProgressBar progress={progress} />}
                    {sizeError && <p className="text-red-400 text-xs mt-1">{sizeError}</p>}
                    {!tripId && <p className="text-yellow-500 text-xs mt-1">יש להזין מספר זהות לפני העלאת קבצים</p>}
                  </div>
                )
              }
            </div>
          </div>
          {/* ══ בלוק החזרה ══ */}
          <div className="bg-blue-900/30 border border-blue-700/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-blue-400 text-base">🔄</span>
                <span className="text-blue-300 font-bold text-sm">החזרה</span>
              </div>
              {!readOnly && (
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={!!c.sameReturnLocation}
                    onChange={e => {
                      update("sameReturnLocation", e.target.checked);
                      if (e.target.checked) { update("returnLocation", c.pickupLocation || ""); update("returnMapsLink", c.pickupMapsLink || ""); }
                    }}
                    className="w-4 h-4 accent-yellow-400 rounded" />
                  <span className="text-blue-300 text-xs font-medium">החזרה באותו מקום</span>
                </label>
              )}
            </div>
            {dayOptions.length > 0 && (
              <div className="flex flex-col gap-1">
                <label className="text-blue-200 text-sm font-medium">יום מהמסלול</label>
                {readOnly
                  ? <div className={inputCls(true)}>{c.returnDayIdx !== "" && c.returnDayIdx !== undefined ? dayOptions[Number(c.returnDayIdx)]?.label || "—" : "לא משויך"}</div>
                  : <select value={c.returnDayIdx ?? ""} onChange={e => update("returnDayIdx", e.target.value)} className={inputCls(false)}>
                    <option value="" className="bg-blue-950">— לא משויך —</option>
                    {dayOptions.map(d => <option key={d.idx} value={d.idx} className="bg-blue-950">{d.label}</option>)}
                  </select>
                }
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">🕐 שעת החזרה</label>
              {readOnly
                ? <div className={inputCls(true)} dir="ltr">{c.returnTime || "—"}</div>
                : <div className="flex gap-1.5 items-center" dir="ltr">
                  <select value={c.returnTime ? c.returnTime.split(":")[0] : ""}
                    onChange={e => { const hh = e.target.value, mm = c.returnTime ? c.returnTime.split(":")[1] || "00" : "00"; update("returnTime", hh ? `${hh}:${mm}` : ""); }}
                    className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20 focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
                    <option value="">שע׳</option>
                    {HOURS.map(h => <option key={h} value={h} className="bg-blue-950">{h}</option>)}
                  </select>
                  <span className="text-blue-400 font-bold text-lg">:</span>
                  <select value={c.returnTime ? c.returnTime.split(":")[1] || "" : ""}
                    onChange={e => { const mm = e.target.value, hh = c.returnTime ? c.returnTime.split(":")[0] || "00" : "00"; update("returnTime", hh ? `${hh}:${mm}` : ""); }}
                    className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-2 py-2.5 w-20 focus:outline-none focus:border-yellow-500/70 text-sm appearance-none text-center">
                    <option value="">דק׳</option>
                    {MINUTES.map(m => <option key={m} value={m} className="bg-blue-950">{m}</option>)}
                  </select>
                </div>
              }
            </div>
            {!c.sameReturnLocation ? (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-blue-200 text-sm font-medium">מיקום החזרה</label>
                  <input placeholder="שדה התעופה, כתובת..." value={c.returnLocation || ""} readOnly={readOnly}
                    onChange={e => update("returnLocation", e.target.value)} className={inputCls(readOnly)} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-blue-200 text-sm font-medium">🗺️ לינק Google Maps — החזרה</label>
                  <div className="flex gap-2">
                    <input placeholder="https://maps.google.com/..." value={c.returnMapsLink || ""} readOnly={readOnly}
                      onChange={e => update("returnMapsLink", e.target.value)} className={inputCls(readOnly)} />
                    {c.returnMapsLink && <a href={c.returnMapsLink} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-2 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/40 transition-colors text-sm whitespace-nowrap">
                      <MapPin size={14} /> פתח</a>}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <span className="text-yellow-400 text-xs">📍 {c.pickupLocation || "אותו מקום כמו האיסוף"}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};


const ContactsSection = ({ contacts, setContacts, readOnly }) => {
  const addContact = () => setContacts(c => [...c, EMPTY_CONTACT()]);
  const removeContact = (id) => setContacts(c => c.filter(ct => ct.id !== id));
  const updateContact = (id, field, value) => setContacts(c => c.map(ct => ct.id === id ? { ...ct, [field]: value } : ct));
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <SectionTitle icon={Phone} title="אנשי קשר וספקים" subtitle="ספקים, מדריכים ואנשי קשר מקומיים" />
      <div className="space-y-4">
        {!contacts.length
          ? <p className="text-blue-500 text-center py-6 text-sm">טרם הוספו אנשי קשר</p>
          : contacts.map(ct => (
            <div key={ct.id} className="bg-blue-900/30 border border-blue-700/40 rounded-xl p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <InputField label="שם" placeholder="שם הספק" value={ct.name} readOnly={readOnly}
                  onChange={e => updateContact(ct.id, "name", e.target.value)} />
                <InputField label="תפקיד" placeholder="נהג, מלון, מדריך" value={ct.role} readOnly={readOnly}
                  onChange={e => updateContact(ct.id, "role", e.target.value)} />
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <InputField label="טלפון" placeholder="+39..." type="tel" value={ct.phone} readOnly={readOnly}
                      inputMode="numeric" onChange={e => updateContact(ct.id, "phone", e.target.value.replace(/[^\d+\-\s]/g, ""))} />
                  </div>
                  {ct.phone && <a href={waLink(ct.phone)} target="_blank" rel="noopener noreferrer"
                    className="mb-0 p-3 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/40 transition-colors"
                    title="WhatsApp"><MessageCircle size={20} /></a>}
                  {!readOnly && <button type="button" onClick={() => removeContact(ct.id)}
                    className="p-3 rounded-xl hover:bg-red-500/20 text-blue-500 hover:text-red-400 transition-colors">
                    <Trash2 size={18} /></button>}
                </div>
              </div>
            </div>
          ))
        }
      </div>
      {!readOnly && (
        <button type="button" onClick={addContact}
          className="mt-4 w-full py-3 rounded-xl border-2 border-dashed border-blue-600 text-blue-400
            hover:border-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/5 transition-all duration-200
            flex items-center justify-center gap-2 font-medium">
          <Plus size={18} /> הוסף איש קשר
        </button>
      )}
    </section>
  );
};

const SecuritySection = ({ tripId, initialData, authCode, setAuthCode, docId }) => {
  const [lockData, setLockData] = useState(null);
  const [locking, setLocking] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const flash = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(""), 3000); };
  useEffect(() => {
    if (!tripId || !initialData) return;
    setLockData({ loginAttempts: initialData.loginAttempts || 0, lockedUntil: initialData.lockedUntil || null, lastLogin: initialData.lastLogin || null });
  }, [tripId, initialData]);
  if (!tripId) return null;
  const isLocked = lockData?.lockedUntil && new Date(lockData.lockedUntil) > new Date();
  const handleLock = async () => {
    setLocking(true);
    try {
      const until = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
      const firestoreId = docId || tripId;
      await updateDoc(doc(db, "trips", firestoreId), { lockedUntil: until, loginAttempts: 5 });
      setLockData(p => ({ ...p, lockedUntil: until, loginAttempts: 5 }));
      flash("🔒 המשתמש ננעל בהצלחה");
    } catch (err) { console.error(err); }
    finally { setLocking(false); }
  };
  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const firestoreId = docId || tripId;
      await updateDoc(doc(db, "trips", firestoreId), { lockedUntil: null, loginAttempts: 0 });
      setLockData(p => ({ ...p, lockedUntil: null, loginAttempts: 0 }));
      flash("🔓 הנעילה שוחררה בהצלחה");
    } catch (err) { console.error(err); }
    finally { setUnlocking(false); }
  };
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <SectionTitle icon={Lock} title="אבטחת חשבון" subtitle="קוד אימות, ניסיונות כניסה ונעילות" />
      <div className="flex items-center gap-3 bg-blue-900/40 border border-blue-700/40 rounded-xl px-5 py-4 mb-5">
        <Lock size={18} className="text-yellow-400 shrink-0" />
        <span className="text-blue-300 text-sm">קוד אימות לאפליקציה:</span>
        <span className="text-3xl font-black text-yellow-400 tracking-[0.3em] flex-1">{authCode}</span>
        <button type="button" onClick={() => { navigator.clipboard.writeText(authCode); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          className="p-2 rounded-xl bg-blue-800/40 border border-blue-700/40 text-blue-400 hover:text-yellow-400 hover:border-yellow-500/40 transition-colors" title="העתק">
          {copied ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
        </button>
        <button type="button" onClick={() => { const c = generateCode(); if (setAuthCode) setAuthCode(c); }}
          className="p-2 rounded-xl bg-blue-800/40 border border-blue-700/40 text-blue-400 hover:text-yellow-400 hover:border-yellow-500/40 transition-colors" title="צור קוד חדש">
          <RefreshCw size={16} />
        </button>
      </div>
      <p className="text-blue-500 text-xs mb-5">⚠️ יש להעביר את הקוד למטייל באופן אישי</p>
      {lockData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-blue-900/40 border border-blue-700/40 rounded-xl p-4 flex flex-col gap-1">
              <span className="text-blue-400 text-xs font-medium">ניסיונות כושלים</span>
              <span className={`text-2xl font-black ${lockData.loginAttempts >= 3 ? "text-red-400" : "text-white"}`}>{lockData.loginAttempts} / 5</span>
              <div className="w-full bg-blue-900 rounded-full h-1.5 mt-1">
                <div className={`h-1.5 rounded-full transition-all ${lockData.loginAttempts >= 4 ? "bg-red-500" : lockData.loginAttempts >= 2 ? "bg-yellow-500" : "bg-green-500"}`}
                  style={{ width: `${(lockData.loginAttempts / 5) * 100}%` }} />
              </div>
            </div>
            <div className={`border rounded-xl p-4 flex flex-col gap-1 ${isLocked ? "bg-red-500/10 border-red-500/40" : "bg-green-500/10 border-green-500/30"}`}>
              <span className="text-blue-400 text-xs font-medium">סטטוס</span>
              <span className={`text-lg font-bold ${isLocked ? "text-red-400" : "text-green-400"}`}>{isLocked ? "🔒 נעול" : "🔓 פתוח"}</span>
              {isLocked && <span className="text-red-300 text-xs">עד: {new Date(lockData.lockedUntil).toLocaleString("he-IL")}</span>}
            </div>
            <div className="bg-blue-900/40 border border-blue-700/40 rounded-xl p-4 flex flex-col gap-1">
              <span className="text-blue-400 text-xs font-medium">כניסה אחרונה</span>
              <span className="text-white text-sm font-medium">{lockData.lastLogin ? new Date(lockData.lastLogin).toLocaleString("he-IL") : "לא נרשמה"}</span>
            </div>
          </div>
          {actionMsg && <p className="text-sm mb-3 font-medium text-yellow-400">{actionMsg}</p>}
          <div className="flex gap-3">
            {!isLocked
              ? <button type="button" onClick={handleLock} disabled={locking}
                className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all bg-red-500/15 border border-red-500/40 text-red-400 hover:bg-red-500/25 disabled:opacity-50">
                <Lock size={16} /> {locking ? "נועל..." : "נעל משתמש"}
              </button>
              : <button type="button" onClick={handleUnlock} disabled={unlocking}
                className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/30 disabled:opacity-50">
                <RefreshCw size={16} className={unlocking ? "animate-spin" : ""} /> {unlocking ? "משחרר..." : "שחרר נעילה ואפס ניסיונות"}
              </button>
            }
          </div>
        </>
      )}
    </section>
  );
};

const PrintView = ({ travelerName, tripId, tripTitle, destination, startDate, endDate, travelerPhone, travelerEmail, flights, days, sharedHotel, sharedAccommodation, contacts, car, expenses, numTravelers, exchangeRates, localCurrency, onClose }) => {
  const countryFlag = COUNTRIES.find(c => c.name === destination)?.flag || "🌍";
  const fmtD = (d) => d ? d.split("-").reverse().join("/") : "—";
  const fmtDT = (v) => { if (!v) return "—"; const [d, t] = v.split("T"); return `${fmtD(d)} ${t?.slice(0, 5) || ""}`; };
  const findAirport = code => COMMON_AIRPORTS.find(a => a.iata === code);

  // חישוב מחירים מהוצאות
  const n = Math.max(1, numTravelers || 1);
  const sumCat = (...cats) => (expenses || [])
    .filter(e => cats.includes(e.category))
    .reduce((s, e) => {
      const base = (e.amountILS != null && e.amountILS > 0)
        ? parseFloat(e.amountILS)
        : (() => {
          const amt = parseFloat(e.amount) || 0;
          if (!amt) return 0;
          const cur = e.currency || "LOCAL";
          if (cur === "ILS") return amt;
          if (cur === "USD" && exchangeRates?.USD) return amt * exchangeRates.USD;
          if (cur === "LOCAL" && localCurrency && exchangeRates?.[localCurrency]) return amt * exchangeRates[localCurrency];
          if (cur === "EUR" && exchangeRates?.EUR) return amt * exchangeRates.EUR;
          return amt;
        })();
      return s + (e.perPerson ? base * n : base);
    }, 0);

  const flightCost = sumCat("flight");
  const carOnlyCost = sumCat("car");
  const fuelCost = sumCat("fuel");
  const carCost = carOnlyCost + fuelCost;
  const hasFuel = fuelCost > 0;
  const hotelExpCost = sumCat("hotel");
  const hotelAccCost = sharedAccommodation?.totalPrice ? parseFloat(sharedAccommodation.totalPrice) || 0 : 0;
  // אם יש הוצאת hotel ב-expenses — השתמש בה. אחרת השתמש ב-totalPrice מהמלון
  const hotelCost = hotelExpCost > 0 ? hotelExpCost : hotelAccCost;
  const otherCost = sumCat("taxi", "shopping", "restaurant", "other");
  const grandTotal = flightCost + carCost + hotelCost + otherCost;

  const decAcriss = decodeAcriss(car?.carClass || "");

  return (
    <div className="fixed inset-0 z-50 bg-white text-gray-900 overflow-y-auto" dir="rtl">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { font-family: Arial, sans-serif; margin: 0; }
          @page { size: A4; margin: 18mm 15mm 20mm 15mm; }
          .page-break { page-break-before: always; }
        }
        .a4-page { max-width: 210mm; margin: 0 auto; padding: 12mm 14mm; }
        .section-card { border: 1px solid #e5e7eb; border-radius: 10px; margin-bottom: 14px; overflow: hidden; }
        .section-header { background: #f1f5f9; padding: 8px 14px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #e5e7eb; }
        .section-body { padding: 10px 14px; }
        .price-badge { background: #fef3c7; border: 1px solid #fcd34d; color: #92400e; font-weight: 700; padding: 2px 10px; border-radius: 20px; font-size: 12px; margin-right: auto; }
        .doc-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #1B3F8B; }
        .doc-footer { text-align: center; color: #9ca3af; font-size: 10px; padding: 8px 0; border-top: 1px solid #e5e7eb; margin-top: 20px; }
      `}</style>

      {/* סרגל פעולות — לא מודפס */}
      <div className="no-print sticky top-0 bg-gray-100 border-b border-gray-200 px-6 py-3 flex items-center gap-3 z-10">
        <button onClick={() => {
          const content = document.getElementById('print-content').innerHTML;
          const w = window.open('', '_blank', 'width=900,height=700');
          w.document.write(`<!DOCTYPE html><html dir="rtl"><head>
            <meta charset="utf-8"/>
            <title>תיק טיול — ${travelerName}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 0; direction: rtl; }
              @page { size: A4; margin: 18mm 15mm 20mm 15mm; }
              * { box-sizing: border-box; }
              .a4-page { max-width: 210mm; margin: 0 auto; padding: 12mm 14mm; }
              .section-card { border: 1px solid #e5e7eb; border-radius: 10px; margin-bottom: 14px; overflow: hidden; }
              .section-header { background: #f1f5f9; padding: 8px 14px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #e5e7eb; }
              .section-body { padding: 10px 14px; }
              .price-badge { background: #fef3c7; border: 1px solid #fcd34d; color: #92400e; font-weight: 700; padding: 2px 10px; border-radius: 20px; font-size: 12px; margin-right: auto; }
              .doc-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #1B3F8B; }
              .doc-footer { text-align: center; color: #9ca3af; font-size: 10px; padding: 8px 0; border-top: 1px solid #e5e7eb; margin-top: 20px; }
            </style>
          </head><body>${content}</body></html>`);
          w.document.close();
          w.focus();
          setTimeout(() => { w.print(); }, 500);
        }}
          className="flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors">
          <Printer size={16} /> הדפס / שמור PDF
        </button>
        <button onClick={onClose}
          className="flex items-center gap-2 px-5 py-2 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-200 transition-colors">
          ✕ סגור
        </button>
        <span className="text-gray-500 text-sm">תצוגת הדפסה — {travelerName} | A4</span>
      </div>

      {/* גוף המסמך */}
      <div id="print-content" className="a4-page">

        {/* ── HEADER ── */}
        <div className="doc-header">
          <div>
            <h1 className="text-2xl font-black text-[#0D1F4A] mb-1">{tripTitle || destination} {countryFlag}</h1>
            <div className="text-gray-600 text-sm space-y-0.5">
              <div>מטייל: <b>{travelerName}</b> &nbsp;|&nbsp; ת.ז: {tripId}</div>
              <div dir="ltr" className="text-right" style={{ direction: "rtl" }}>📅 {fmtD(startDate)} — {fmtD(endDate)}</div>
              {n > 1 && <div>👥 {n} משתתפים</div>}
              {travelerPhone && <div>📞 {travelerPhone}</div>}
              {travelerEmail && <div>✉️ {travelerEmail}</div>}
            </div>
          </div>
          <div style={{ textAlign: "left" }}>
            <img src="/megedLogoTransp.png" alt="מגד שמים"
              style={{ width: "80px", height: "80px", objectFit: "contain" }} />
            <div style={{ fontSize: "11px", color: "#1B3F8B", fontWeight: "700", textAlign: "center", marginTop: "2px" }}>מגד שמים</div>
          </div>
        </div>

        {/* ── סקשן טיסה ── */}
        {flights.some(f => f.departureAirport || f.arrivalAirport) && (
          <div className="section-card">
            <div className="section-header">
              <span style={{ fontSize: "18px" }}>✈️</span>
              <span style={{ fontWeight: "700", color: "#0D1F4A" }}>פרטי טיסה</span>
              {flightCost > 0 && <span className="price-badge">₪ {flightCost.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</span>}
            </div>
            <div className="section-body">
              {flights.map((f, idx) => (f.departureAirport || f.arrivalAirport) ? (
                <div key={f.id || idx} style={{ marginBottom: idx < flights.length - 1 ? "10px" : 0, paddingBottom: idx < flights.length - 1 ? "10px" : 0, borderBottom: idx < flights.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                  <div style={{ fontWeight: "600", color: "#374151", marginBottom: "6px", fontSize: "12px" }}>
                    {idx === 0 ? "✈️ הלוך" : idx === 1 ? "🔄 חזור" : `טיסה ${idx + 1}`}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", fontSize: "12px", color: "#4b5563" }}>
                    <div>מוצא: <b>{f.departureAirport}{findAirport(f.departureAirport)?.city ? ` — ${findAirport(f.departureAirport).city}` : ""}</b></div>
                    <div>יעד: <b>{f.arrivalAirport}{findAirport(f.arrivalAirport)?.city ? ` — ${findAirport(f.arrivalAirport).city}` : ""}</b></div>
                    {f.airline && <div>חברה: <b>{f.airlineCustom || f.airline}</b></div>}
                    {f.flightNumber && <div>מספר טיסה: <b>{f.flightNumber}</b></div>}
                    {f.departureTime && <div>המראה: <b>{fmtDT(f.departureTime)}</b></div>}
                    {f.arrivalTime && <div>נחיתה: <b>{fmtDT(f.arrivalTime)}</b></div>}
                  </div>
                </div>
              ) : null)}
            </div>
          </div>
        )}

        {/* ── סקשן מלון ── */}
        {sharedAccommodation?.hotelName && (
          <div className="section-card">
            <div className="section-header">
              <span style={{ fontSize: "18px" }}>🏨</span>
              <span style={{ fontWeight: "700", color: "#0D1F4A" }}>לינה</span>
              {hotelCost > 0 && <span className="price-badge">₪ {hotelCost.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</span>}
            </div>
            <div className="section-body" style={{ fontSize: "13px", color: "#374151" }}>
              <div style={{ fontSize: "15px", fontWeight: "700", marginBottom: "6px" }}>{sharedAccommodation.hotelName}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", fontSize: "12px", color: "#4b5563" }}>
                {sharedAccommodation.address && <div style={{ gridColumn: "1/-1" }}>📍 {sharedAccommodation.address}</div>}
                {sharedAccommodation.checkoutTime && <div>Checkout: <b>{sharedAccommodation.checkoutTime}</b></div>}
                {sharedAccommodation.bookingRating && <div>דירוג Booking: <b>{sharedAccommodation.bookingRating} ★</b></div>}
                {sharedAccommodation.freeCancellation != null && (
                  <div style={{ color: sharedAccommodation.freeCancellation ? "#16a34a" : "#dc2626" }}>
                    {sharedAccommodation.freeCancellation ? "✅ ביטול חינם" : "❌ ללא ביטול חינם"}
                  </div>
                )}
                {sharedAccommodation.amenities && <div style={{ gridColumn: "1/-1", color: "#6b7280" }}>{sharedAccommodation.amenities}</div>}
              </div>
            </div>
          </div>
        )}

        {/* ── סקשן רכב ── */}
        {(car?.company || car?.pickupLocation || car?.carClass) && (
          <div className="section-card">
            <div className="section-header">
              <span style={{ fontSize: "18px" }}>🚗</span>
              <span style={{ fontWeight: "700", color: "#0D1F4A" }}>רכב שכור</span>
              {carCost > 0 && (
                <span className="price-badge">
                  ₪ {carCost.toLocaleString("he-IL", { maximumFractionDigits: 0 })}
                  <span style={{ fontWeight: "400", marginRight: "4px" }}>({hasFuel ? "כולל דלק" : "לא כולל דלק"})</span>
                </span>
              )}
            </div>
            <div className="section-body" style={{ fontSize: "12px", color: "#4b5563" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px" }}>
                {car.company && <div>חברה: <b>{car.company}</b></div>}
                {car.carClass && (
                  <div>קלאס: <b style={{ letterSpacing: "0.15em" }}>{car.carClass}</b>
                    {decAcriss && <span style={{ color: "#6b7280" }}> — {[decAcriss.c1?.label, decAcriss.c2?.label, decAcriss.c3?.label, decAcriss.c4?.label].filter(Boolean).join(" | ")}</span>}
                  </div>
                )}
              </div>
              {(car.pickupLocation || car.pickupTime || car.pickupDayIdx != null) && (
                <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f3f4f6" }}>
                  <div style={{ fontWeight: "600", color: "#374151", marginBottom: "3px" }}>📍 איסוף</div>
                  {car.pickupDayIdx != null && days?.[car.pickupDayIdx] && (
                    <div>יום {parseInt(car.pickupDayIdx) + 1}{days[car.pickupDayIdx].date ? ` — ${fmtD(days[car.pickupDayIdx].date)}` : ""}</div>
                  )}
                  {car.pickupTime && <div>🕐 שעה: <b>{car.pickupTime}</b></div>}
                  {car.pickupLocation && <div>📌 {car.pickupLocation}</div>}
                </div>
              )}
              {(car.returnLocation || car.returnTime || car.returnDayIdx != null || car.sameReturnLocation) && (
                <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f3f4f6" }}>
                  <div style={{ fontWeight: "600", color: "#374151", marginBottom: "3px" }}>🔄 החזרה</div>
                  {car.returnDayIdx != null && days?.[car.returnDayIdx] && (
                    <div>יום {parseInt(car.returnDayIdx) + 1}{days[car.returnDayIdx].date ? ` — ${fmtD(days[car.returnDayIdx].date)}` : ""}</div>
                  )}
                  {car.returnTime && <div>🕐 שעה: <b>{car.returnTime}</b></div>}
                  {car.sameReturnLocation
                    ? <div>📌 {car.pickupLocation || "אותו מקום כמו האיסוף"}</div>
                    : car.returnLocation && <div>📌 {car.returnLocation}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── סקשן מסלול ── */}
        {days.length > 0 && (
          <div className="section-card">
            <div className="section-header">
              <span style={{ fontSize: "18px" }}>📅</span>
              <span style={{ fontWeight: "700", color: "#0D1F4A" }}>מסלול הטיול</span>
            </div>
            <div className="section-body" style={{ padding: "0" }}>
              {days.map((day, idx) => (
                <div key={day.id || idx} style={{ borderBottom: idx < days.length - 1 ? "1px solid #f3f4f6" : "none", padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                    <span style={{ background: "#1B3F8B", color: "white", borderRadius: "6px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>
                      יום {idx + 1}
                    </span>
                    <span style={{ fontWeight: "600", color: "#0D1F4A", fontSize: "13px" }}>{fmtD(day.date)}</span>
                    {day.dayTitle && <span style={{ color: "#6b7280", fontSize: "12px" }}>— {day.dayTitle}</span>}
                  </div>
                  {day.activities?.filter(a => (a.title || a.time) && a.type !== "travel").map((act, aIdx) => (
                    <div key={act.id || aIdx} style={{ display: "flex", gap: "10px", fontSize: "12px", marginBottom: "4px" }}>
                      {act.time && <span style={{ color: "#9ca3af", fontFamily: "monospace", minWidth: "40px" }}>{act.time}</span>}
                      <div>
                        {act.title && <span style={{ fontWeight: "600", color: "#1f2937" }}>{act.title}</span>}
                        {act.description && <span style={{ color: "#6b7280" }}> — {act.description}</span>}
                      </div>
                    </div>
                  ))}
                  {day.activities?.filter(a => a.type === "travel" && a.duration).map((tr, tIdx) => (
                    <div key={tIdx} style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "3px", paddingRight: "8px" }}>
                      🚗 נסיעה משוערת: {tr.duration} דקות{tr.note ? ` — ${tr.note}` : ""}
                    </div>
                  ))}
                  {/* שם מלון בתוך היום */}
                  {(() => {
                    const accName = !sharedHotel && day.accommodation?.hotelName
                      ? day.accommodation.hotelName
                      : (sharedHotel && sharedAccommodation?.hotelName ? sharedAccommodation.hotelName : null);
                    return accName ? (
                      <div style={{ marginTop: "4px", fontSize: "11px", color: "#6b7280", borderTop: "1px solid #f9fafb", paddingTop: "4px" }}>
                        🏨 {accName}
                      </div>
                    ) : null;
                  })()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── סיכום סופי ── */}
        {grandTotal > 0 && (
          <div className="section-card" style={{ background: "#fafafa", pageBreakInside: "avoid", breakInside: "avoid" }}>
            <div className="section-header" style={{ background: "#1B3F8B" }}>
              <span style={{ fontSize: "18px" }}>💰</span>
              <span style={{ fontWeight: "700", color: "white" }}>סיכום עלויות</span>
            </div>
            <div className="section-body">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", fontSize: "12px", color: "#374151", marginBottom: "10px" }}>
                {flightCost > 0 && <><div>✈️ טיסות</div><div style={{ textAlign: "left", fontWeight: "600" }}>₪ {flightCost.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</div></>}
                {hotelCost > 0 && <><div>🏨 לינה</div><div style={{ textAlign: "left", fontWeight: "600" }}>₪ {hotelCost.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</div></>}
                {carCost > 0 && <><div>🚗 רכב{hasFuel ? " (כולל דלק)" : ""}</div><div style={{ textAlign: "left", fontWeight: "600" }}>₪ {carCost.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</div></>}
                {otherCost > 0 && <><div>📌 שונות</div><div style={{ textAlign: "left", fontWeight: "600" }}>₪ {otherCost.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</div></>}
              </div>
              <div style={{ borderTop: "2px solid #1B3F8B", paddingTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: "700", color: "#0D1F4A", fontSize: "14px" }}>סה"כ כולל</span>
                <span style={{ fontWeight: "900", color: "#D4A017", fontSize: "20px" }}>₪ {grandTotal.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</span>
              </div>
              {n > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "6px", color: "#6b7280", fontSize: "12px" }}>
                  <span>לאדם ({n} משתתפים)</span>
                  <span style={{ fontWeight: "700", color: "#374151", fontSize: "14px" }}>₪ {Math.round(grandTotal / n).toLocaleString("he-IL")}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── FOOTER ── */}
        <div className="doc-footer">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>מגד שמים — תיק טיול של {travelerName}</span>
            <span>הופק: {new Date().toLocaleDateString("he-IL")}</span>
          </div>
          <div style={{ marginTop: "2px", color: "#d1d5db", fontSize: "9px" }}>
            הדפסה: {new Date().toLocaleString("he-IL")}
          </div>
        </div>

      </div>
    </div>
  );
};

const EMPTY_EXPENSE = () => ({
  id: `exp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  date: new Date().toISOString().split("T")[0],
  category: "", customCategory: "", amount: "", currency: "LOCAL", note: "", amountILS: null,
  source: "dashboard",
  perPerson: false,   // false = כל הקבוצה, true = לאדם (× מספר משתתפים)
});

const ExpenseSection = ({ expenses, setExpenses, localCurrency, exchangeRates, readOnly, sharedAccommodation, numTravelers }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_EXPENSE());
  const today = new Date().toISOString().split("T")[0];
  const localCur = CURRENCIES.find(c => c.code === localCurrency);
  const getCurSymbol = (currency) => { if (currency === "ILS") return "₪"; if (currency === "USD") return "$"; return localCur?.symbol || "?"; };
  const toILS = (amount, currency) => {
    if (!amount || !exchangeRates) return null;
    const num = parseFloat(amount);
    if (isNaN(num)) return null;
    if (currency === "ILS") return num;
    const code = currency === "USD" ? "USD" : localCurrency;
    const rate = exchangeRates[code];
    if (!rate) return null;
    return parseFloat((num * rate).toFixed(2));
  };
  const handleAdd = () => {
    if (!draft.category || !draft.amount || isNaN(parseFloat(draft.amount))) return;
    const ils = toILS(draft.amount, draft.currency);
    setExpenses(prev => [...prev, { ...draft, id: Date.now() + Math.random(), amountILS: ils }]);
    setDraft(EMPTY_EXPENSE()); setAdding(false);
  };
  const handleRemove = (id) => setExpenses(prev => prev.filter(e => e.id !== id));
  const [perPersonView, setPerPersonView] = useState(false);
  const n = Math.max(1, numTravelers || 1);
  const hotelILS = sharedAccommodation?.totalPrice ? parseFloat(sharedAccommodation.totalPrice) || 0 : 0;
  const calcBase = (e) => {
    // מחזיר את הסכום הבסיסי ב-ILS לפני חישוב perPerson
    if (e.amountILS != null && e.amountILS > 0) return e.amountILS;
    const amt = parseFloat(e.amount) || 0; if (!amt) return 0;
    const cur = e.currency || "LOCAL";
    if (cur === "ILS") return amt;
    if (cur === "USD" && exchangeRates?.USD) return amt * exchangeRates.USD;
    if (cur === "LOCAL" && localCurrency && exchangeRates?.[localCurrency]) return amt * exchangeRates[localCurrency];
    return 0;
  };
  const calcILS = (e) => e.perPerson ? calcBase(e) * n : calcBase(e);
  const totalILS = hotelILS + expenses.reduce((sum, e) => sum + calcILS(e), 0);
  const totalPerPerson = (hotelILS / n) + expenses.reduce((sum, e) =>
    sum + (e.perPerson ? calcBase(e) : calcBase(e) / n), 0);
  const byCategory = EXPENSE_CATEGORIES.map(cat => ({
    ...cat,
    total: expenses.filter(e => e.category === cat.id).reduce((s, e) => {
      if (e.amountILS != null && e.amountILS > 0) return s + e.amountILS;
      const amt = parseFloat(e.amount) || 0; if (!amt) return s;
      const cur = e.currency || "LOCAL";
      if (cur === "ILS") return s + amt;
      if (cur === "USD" && exchangeRates?.USD) return s + amt * exchangeRates.USD;
      if (cur === "LOCAL" && localCurrency && exchangeRates?.[localCurrency]) return s + amt * exchangeRates[localCurrency];
      return s;
    }, 0),
    count: expenses.filter(e => e.category === cat.id).length,
  })).filter(c => c.count > 0);
  const getCatLabel = (e) => { if (e.category === "other" && e.customCategory) return e.customCategory; return EXPENSE_CATEGORIES.find(c => c.id === e.category)?.label || e.category; };
  const getCatIcon = (catId) => EXPENSE_CATEGORIES.find(c => c.id === catId)?.icon || "📌";
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-5">
        <SectionTitle icon={({ size, className }) => <span className={className} style={{ fontSize: size }}>💰</span>}
          title="יומן הוצאות"
          subtitle={localCurrency ? `מטבע: ${localCur?.symbol} ${localCur?.name}` : "לא הוגדר מטבע מקומי"} />
        <div className="flex items-center gap-2">
          {n > 1 && (
            <div className="flex items-center gap-1 bg-blue-900/30 border border-blue-700/30 rounded-xl p-1">
              <button type="button" onClick={() => setPerPersonView(false)}
                className={"px-3 py-1.5 rounded-lg text-xs font-semibold transition-all " + (!perPersonView ? "bg-[#D4A017]/20 border border-[#D4A017]/40 text-[#D4A017]" : "text-blue-400 hover:text-white")}>
                סה"כ
              </button>
              <button type="button" onClick={() => setPerPersonView(true)}
                className={"px-3 py-1.5 rounded-lg text-xs font-semibold transition-all " + (perPersonView ? "bg-[#D4A017]/20 border border-[#D4A017]/40 text-[#D4A017]" : "text-blue-400 hover:text-white")}>
                לאדם
              </button>
            </div>
          )}
          {!readOnly && (
            <button type="button" onClick={() => setAdding(a => !a)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/35 text-sm font-semibold transition-all">
              <Plus size={16} /> הוסף הוצאה
            </button>
          )}
        </div>
      </div>
      {expenses.length > 0 && (
        <div className="bg-gradient-to-l from-[#D4A017]/10 to-transparent border border-[#D4A017]/20 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-blue-300 text-sm font-medium">{perPersonView && n > 1 ? `לאדם (מתוך ${n})` : 'סה"כ הטיול'}</span>
              {n > 1 && !perPersonView && (
                <div className="text-blue-500 text-xs mt-0.5">לאדם: ₪ {totalPerPerson.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</div>
              )}
            </div>
            <span className="text-3xl font-black text-[#D4A017]">
              ₪ {(perPersonView && n > 1 ? totalPerPerson : totalILS).toLocaleString("he-IL", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {byCategory.map(cat => (
              <div key={cat.id} className="bg-blue-900/40 rounded-xl p-3 text-center">
                <div className="text-lg mb-1">{cat.icon}</div>
                <div className="text-white text-xs font-semibold">{cat.label}</div>
                <div className="text-[#D4A017] text-sm font-bold">₪ {cat.total.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</div>
                <div className="text-blue-500 text-xs">{cat.count} פריטים</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {adding && !readOnly && (
        <div className="bg-blue-900/30 border border-blue-700/40 rounded-2xl p-5 mb-5">
          <h4 className="text-white font-semibold mb-4 text-sm">הוצאה חדשה</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <div className="flex flex-col gap-1">
              <label className="text-blue-300 text-xs">קטגוריה *</label>
              <select value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
                className={`bg-blue-900/40 border text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500/70 appearance-none
                  ${!draft.category ? "border-yellow-500/50" : "border-blue-700/50"}`}>
                <option value="" className="bg-blue-950">— בחר קטגוריה —</option>
                {EXPENSE_CATEGORIES.map(c => <option key={c.id} value={c.id} className="bg-blue-950">{c.icon} {c.label}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-300 text-xs">תאריך</label>
              <EuDateInput value={draft.date} max={today} onChange={v => setDraft(d => ({ ...d, date: v || today }))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-300 text-xs">מטבע</label>
              <div className="flex gap-1">
                {[{ code: "LOCAL", label: localCur?.symbol || "מקומי" }, { code: "USD", label: "$" }, { code: "ILS", label: "₪" }].map(opt => (
                  <button key={opt.code} type="button" onClick={() => setDraft(d => ({ ...d, currency: opt.code }))}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all
                      ${draft.currency === opt.code ? "bg-[#D4A017]/20 border-[#D4A017]/60 text-[#D4A017]"
                        : "bg-blue-900/40 border-blue-700/50 text-blue-400 hover:border-blue-500"}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-300 text-xs">סכום ({getCurSymbol(draft.currency)})</label>
              <input type="number" min="0" step="0.01" placeholder="0.00" value={draft.amount}
                onChange={e => setDraft(d => ({ ...d, amount: e.target.value }))}
                className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500/70" dir="ltr" />
            </div>
            {draft.category === "other" && (
              <div className="flex flex-col gap-1 md:col-span-2">
                <label className="text-blue-300 text-xs">פירוט הוצאה</label>
                <input type="text" placeholder="תיאור ההוצאה..." value={draft.customCategory}
                  onChange={e => setDraft(d => ({ ...d, customCategory: e.target.value }))}
                  className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500/70" />
              </div>
            )}
            <div className="flex flex-col gap-1 md:col-span-3">
              <label className="text-blue-300 text-xs">הערה (אופציונלי)</label>
              <input type="text" placeholder="הערה..." value={draft.note}
                onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}
                className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500/70" />
            </div>
            {n > 1 && (
              <div className="flex flex-col gap-1 md:col-span-3">
                <label className="text-blue-300 text-xs">חלוקה</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setDraft(d => ({ ...d, perPerson: false }))}
                    className={"flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all " + (!draft.perPerson ? "bg-[#D4A017]/20 border-[#D4A017]/60 text-[#D4A017]" : "bg-blue-900/40 border-blue-700/50 text-blue-400 hover:border-blue-500")}>
                    👥 כל הקבוצה
                  </button>
                  <button type="button" onClick={() => setDraft(d => ({ ...d, perPerson: true }))}
                    className={"flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all " + (draft.perPerson ? "bg-[#D4A017]/20 border-[#D4A017]/60 text-[#D4A017]" : "bg-blue-900/40 border-blue-700/50 text-blue-400 hover:border-blue-500")}>
                    👤 לאדם (×{n})
                  </button>
                </div>
              </div>
            )}
          </div>
          {draft.amount && draft.currency !== "ILS" && toILS(draft.amount, draft.currency) && (
            <p className="text-[#D4A017]/80 text-sm mb-3">≈ ₪ {toILS(draft.amount, draft.currency).toLocaleString("he-IL", { minimumFractionDigits: 2 })}</p>
          )}
          {!draft.category && <p className="text-yellow-500/80 text-xs mb-2">⚠️ יש לבחור קטגוריה</p>}
          <div className="flex gap-2">
            <button type="button" onClick={handleAdd} disabled={!draft.category}
              className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all border
                ${draft.category ? "bg-[#D4A017]/20 border-[#D4A017]/40 text-[#D4A017] hover:bg-[#D4A017]/30"
                  : "bg-blue-900/20 border-blue-800/30 text-blue-700 cursor-not-allowed"}`}>
              הוסף
            </button>
            <button type="button" onClick={() => { setAdding(false); setDraft(EMPTY_EXPENSE()); }}
              className="px-5 py-2.5 rounded-xl bg-blue-800/30 border border-blue-700/40 text-blue-300 hover:text-white text-sm transition-all">
              ביטול
            </button>
          </div>
        </div>
      )}
      {/* כרטיס מחיר מלון */}
      {sharedAccommodation?.totalPrice && (
        <div className="flex items-center gap-3 bg-blue-900/30 border border-blue-700/30 rounded-xl px-4 py-3 mb-3">
          <span className="text-xl shrink-0">🏨</span>
          <div className="flex-1 min-w-0">
            <span className="text-white text-sm font-semibold">
              {sharedAccommodation.hotelName ? `מלון — ${sharedAccommodation.hotelName}` : "לינה"}
            </span>
            <div className="text-blue-500 text-xs">עלות לינה כוללת</div>
          </div>
          <div className="text-left shrink-0">
            <div className="text-white text-sm font-bold">₪ {parseFloat(sharedAccommodation.totalPrice).toLocaleString("he-IL")}</div>
          </div>
        </div>
      )}
      {expenses.length === 0
        ? <div className="text-center py-10 text-blue-600">
          <span className="text-4xl block mb-2">💸</span>
          <p className="text-sm mb-4">אין הוצאות עדיין</p>
          {!readOnly && !adding && (
            <button type="button" onClick={() => setAdding(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/35 text-sm font-semibold transition-all mx-auto">
              <Plus size={16} /> הוסף הוצאה ראשונה
            </button>
          )}
        </div>
        : <div className="space-y-2">
          {[...expenses].sort((a, b) => a.date > b.date ? -1 : 1).map(e => (
            <div key={e.id} className="flex items-center gap-3 bg-blue-900/30 border border-blue-700/30 rounded-xl px-4 py-3">
              <span className="text-xl shrink-0">{getCatIcon(e.category)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-sm font-semibold">{getCatLabel(e)}</span>
                  {e.perPerson && n > 1 && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-800/60 text-blue-300">👤 לאדם ×{n}</span>}
                  {!e.perPerson && n > 1 && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-800/60 text-blue-300">👥 קבוצה</span>}
                  {e.note && <span className="text-blue-400 text-xs truncate">{e.note}</span>}
                </div>
                <span className="text-blue-500 text-xs">{e.date?.split("-").reverse().join("/")}</span>
              </div>
              <div className="text-left shrink-0">
                <div className="text-white text-sm font-bold">{getCurSymbol(e.currency || "LOCAL")} {parseFloat(e.amount).toLocaleString()}</div>
                {e.amountILS && <div className="text-[#D4A017] text-xs">₪ {e.amountILS.toLocaleString("he-IL", { maximumFractionDigits: 0 })}</div>}
              </div>
              {(!readOnly || e.source !== "dashboard") && (
                <button type="button" onClick={() => handleRemove(e.id)}
                  className="text-blue-600 hover:text-red-400 transition-colors shrink-0"
                  title={e.source === "dashboard" ? "מחק הוצאה" : "מחק (הוכנס מהאפליקציה)"}>
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      }
    </section>
  );
};

const TIP_CATEGORIES = [
  { id: "food", label: "אוכל", icon: "🍽️" },
  { id: "transport", label: "תחבורה", icon: "🚌" },
  { id: "safety", label: "בטיחות", icon: "🛡️" },
  { id: "general", label: "כללי", icon: "💡" },
  { id: "money", label: "כסף", icon: "💵" },
  { id: "culture", label: "תרבות", icon: "🎭" },
];

const TipsSection = ({ tips, setTips, readOnly, destination }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ category: "general", text: "" });
  const handleAdd = () => { if (!draft.text.trim()) return; setTips(prev => [...prev, { id: Date.now() + Math.random(), ...draft }]); setDraft({ category: "general", text: "" }); setAdding(false); };
  const handleRemove = (id) => setTips(prev => prev.filter(t => t.id !== id));
  const getCat = id => TIP_CATEGORIES.find(c => c.id === id) || TIP_CATEGORIES[3];
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-5">
        <SectionTitle icon={({ size, className }) => <span className={className} style={{ fontSize: size }}>💡</span>}
          title="טיפים למטייל" subtitle={destination ? `טיפים ייחודיים ל${destination}` : "טיפים ומידע שימושי"} />
        {!readOnly && (
          <button type="button" onClick={() => setAdding(a => !a)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/25 text-sm font-semibold transition-all">
            <Plus size={16} /> הוסף טיפ
          </button>
        )}
      </div>
      {adding && !readOnly && (
        <div className="bg-blue-900/30 border border-blue-700/40 rounded-2xl p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div className="flex flex-col gap-1">
              <label className="text-blue-300 text-xs">קטגוריה</label>
              <select value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
                className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500/70 appearance-none">
                {TIP_CATEGORIES.map(c => <option key={c.id} value={c.id} className="bg-blue-950">{c.icon} {c.label}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1 md:col-span-2">
              <label className="text-blue-300 text-xs">הטיפ</label>
              <input type="text" placeholder="כתוב טיפ שימושי..." value={draft.text}
                onChange={e => setDraft(d => ({ ...d, text: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && handleAdd()}
                className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500/70" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={handleAdd}
              className="px-5 py-2.5 rounded-xl bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/30 font-semibold text-sm transition-all">הוסף</button>
            <button type="button" onClick={() => setAdding(false)}
              className="px-5 py-2.5 rounded-xl bg-blue-800/30 border border-blue-700/40 text-blue-300 hover:text-white text-sm transition-all">ביטול</button>
          </div>
        </div>
      )}
      {tips.length === 0
        ? <div className="text-center py-10 text-blue-600"><span className="text-4xl block mb-2">💡</span><p className="text-sm">אין טיפים עדיין</p></div>
        : <div className="space-y-2">
          {TIP_CATEGORIES.map(cat => {
            const catTips = tips.filter(t => t.category === cat.id);
            if (!catTips.length) return null;
            return (
              <div key={cat.id}>
                <div className="flex items-center gap-2 mb-2 mt-3">
                  <span>{cat.icon}</span>
                  <span className="text-blue-300 text-xs font-semibold uppercase tracking-wider">{cat.label}</span>
                </div>
                {catTips.map(tip => (
                  <div key={tip.id} className="flex items-start gap-3 bg-blue-900/30 border border-blue-700/30 rounded-xl px-4 py-3 mb-1.5">
                    <span className="text-yellow-400 mt-0.5 shrink-0">•</span>
                    <span className="text-white text-sm flex-1">{tip.text}</span>
                    {!readOnly && (
                      <button type="button" onClick={() => handleRemove(tip.id)}
                        className="text-blue-600 hover:text-red-400 transition-colors shrink-0"><Trash2 size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      }
    </section>
  );
};

const DestinationSearch = ({ value, onChange }) => {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const ref = useRef(null);
  useEffect(() => { setQuery(value || ""); }, [value]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const search = (q) => {
    setQuery(q);
    if (!q.trim()) { setResults(COUNTRIES); setOpen(true); return; }
    const lower = q.toLowerCase();
    setResults(COUNTRIES.filter(c => c.name.includes(q) || c.name.toLowerCase().includes(lower)));
    setOpen(true);
  };
  const select = (country) => { onChange(country.name); setQuery(country.name); setOpen(false); };
  const selected = COUNTRIES.find(c => c.name === value);
  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-2 relative">
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xl pointer-events-none z-10">{selected?.flag || "🌍"}</span>
        <input value={query} onChange={e => search(e.target.value)}
          onFocus={() => { setResults(query ? COUNTRIES.filter(c => c.name.includes(query)) : COUNTRIES); setOpen(true); }}
          placeholder="חפש יעד..." className={`${inputCls(false)} pr-10`} dir="rtl" />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-[#0a1628] border border-blue-700/60 rounded-xl shadow-2xl max-h-56 overflow-y-auto">
          {(results.length > 0 ? results : COUNTRIES).map(c => (
            <div key={c.name} onClick={() => select(c)}
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-blue-800/40 transition-colors
                ${c.name === value ? "bg-[#1B3F8B]/60 border-r-2 border-[#D4A017]" : ""}`}>
              <span className="text-xl">{c.flag}</span>
              <span className="text-white text-sm">{c.name}</span>
              <span className="text-blue-500 text-xs mr-auto">{c.currency}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CurrencyCard = ({ isActive, onClick, flag, symbol, name, big, value, inputVal, onInputChange, inputRef }) => (
  <div onClick={onClick} data-calculator="true"
    className={`rounded-2xl cursor-text transition-all border-2 ${big ? "p-4" : "p-3"}
      ${isActive ? "bg-[#1B3F8B] border-[#D4A017]/60 shadow-lg shadow-[#D4A017]/10" : "bg-blue-900/30 border-blue-700/30 hover:border-blue-600/50"}`}>
    <div className={`flex items-center gap-1.5 ${big ? "mb-3" : "mb-2"}`}>
      {flag && <span className={big ? "text-xl" : "text-sm"}>{flag}</span>}
      <span className={`font-black ${big ? "text-2xl" : "text-base"} ${isActive ? "text-[#D4A017]" : "text-blue-300"}`}>{symbol}</span>
      <span className={`text-blue-400 ${big ? "text-xs" : "text-[10px]"}`}>{name}</span>
    </div>
    <input ref={isActive ? inputRef : null} type="text" inputMode="decimal" placeholder="0"
      value={isActive ? inputVal : (value || "")} readOnly={!isActive}
      onChange={e => { const raw = e.target.value.replace(/[^\d.]/g, "").replace(/(\.\d*)\./g, "$1"); onInputChange(raw); }}
      className={`w-full bg-transparent focus:outline-none font-black tracking-wide ${big ? "text-4xl" : "text-xl"}
        ${isActive ? "text-white" : "text-blue-300/70"} ${!isActive ? "cursor-pointer" : ""}`} dir="ltr" />
  </div>
);

const CurrencyCalculator = ({ localCurrency, setLocalCurrency, exchangeRates, loadingRates, ratesError, readOnly }) => {
  const [inputVal, setInputVal] = useState("");
  const [activeField, setActiveField] = useState("LOCAL");
  const inputRef = useRef(null);
  const localCur = CURRENCIES.find(c => c.code === localCurrency);
  const toILSRate = code => { if (!exchangeRates) return null; if (code === "ILS") return 1; return exchangeRates[code] || null; };
  const convert = (amount, fromCode, toCode) => {
    if (!amount || isNaN(parseFloat(amount))) return "";
    const num = parseFloat(amount);
    const from = toILSRate(fromCode), to = toILSRate(toCode);
    if (!from || !to) return "";
    return ((num * from) / to).toFixed(2);
  };
  const fromCode = activeField === "LOCAL" ? (localCurrency || "ILS") : activeField === "USD" ? "USD" : activeField === "EUR" ? "EUR" : "ILS";
  const getResult = toCode => { if (toCode === fromCode) return inputVal; return convert(inputVal, fromCode, toCode); };
  const ilsResult = getResult("ILS"), usdResult = getResult("USD"), eurResult = getResult("EUR");
  const localResult = localCurrency ? getResult(localCurrency) : "";
  const localIsUSD = localCurrency === "USD", localIsEUR = localCurrency === "EUR", localIsILS = localCurrency === "ILS";
  const handleCardClick = (field) => { setActiveField(field); setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0); };
  const secondSmallCode = localIsUSD ? "EUR" : "USD";
  const secondSmallSymbol = localIsUSD ? "€" : "$";
  const secondSmallName = localIsUSD ? "אירו" : "דולר";
  const secondSmallFlag = localIsUSD ? "🇪🇺" : "🇺🇸";
  const secondSmallResult = localIsUSD ? eurResult : usdResult;
  const showThirdSmall = !localIsUSD && !localIsEUR && !localIsILS;
  return (
    <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
      <SectionTitle icon={Globe} title="מחשבון המרת מטבע" subtitle="לחץ על מטבע והקלד סכום" />
      {!readOnly && (
        <div className="mb-6">
          <label className="text-blue-300 text-sm font-medium block mb-2">מטבע מקומי ליעד</label>
          <select value={localCurrency || ""} onChange={e => { setLocalCurrency(e.target.value); setInputVal(""); setActiveField("LOCAL"); }}
            className="bg-blue-900/40 border border-blue-700/50 text-white rounded-xl px-4 py-3 w-full max-w-xs focus:outline-none focus:border-yellow-500/70 appearance-none text-sm">
            <option value="" className="bg-blue-950">— בחר מטבע —</option>
            {CURRENCIES.filter(c => c.code !== "ILS").map(c => (
              <option key={c.code} value={c.code} className="bg-blue-950">{c.symbol} {c.name} ({c.code})</option>
            ))}
          </select>
        </div>
      )}
      {loadingRates && <p className="text-blue-400 text-sm mb-4 animate-pulse">טוען שערי מטבע...</p>}
      {ratesError && <p className="text-red-400 text-sm mb-4">⚠️ {ratesError}</p>}
      {!localCurrency
        ? <div className="text-center py-10 text-blue-600"><Globe size={44} className="mx-auto mb-3 opacity-20" /><p className="text-sm">בחר מטבע מקומי כדי להפעיל את המחשבון</p></div>
        : <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {!localIsILS && <CurrencyCard isActive={activeField === "LOCAL"} onClick={() => handleCardClick("LOCAL")}
              flag={COUNTRIES.find(c => c.currency === localCurrency)?.flag} symbol={localCur?.symbol || localCurrency}
              name={localCur?.name || localCurrency} value={localResult} inputVal={inputVal}
              onInputChange={setInputVal} inputRef={inputRef} big />}
            <CurrencyCard isActive={activeField === "ILS"} onClick={() => handleCardClick("ILS")}
              flag="🇮🇱" symbol="₪" name="שקל ישראלי" value={ilsResult} inputVal={inputVal}
              onInputChange={setInputVal} inputRef={inputRef} big={localIsILS} />
          </div>
          {exchangeRates && !localIsILS && (
            <p className="text-blue-500 text-xs text-center">
              1 {localCur?.symbol} = ₪ {exchangeRates[localCurrency]?.toFixed(4) || "—"}
              {!localIsUSD && exchangeRates["USD"] && ` · 1$ = ₪ ${exchangeRates["USD"]?.toFixed(4)}`}
              {!localIsEUR && exchangeRates["EUR"] && ` · 1€ = ₪ ${exchangeRates["EUR"]?.toFixed(4)}`}
            </p>
          )}
          <div className={`grid gap-2 pt-1 ${showThirdSmall ? "grid-cols-2" : "grid-cols-1 max-w-[50%]"}`}>
            <CurrencyCard isActive={activeField === secondSmallCode} onClick={() => handleCardClick(secondSmallCode)}
              flag={secondSmallFlag} symbol={secondSmallSymbol} name={secondSmallName}
              value={secondSmallResult} inputVal={inputVal} onInputChange={setInputVal} inputRef={inputRef} />
            {showThirdSmall && <CurrencyCard isActive={activeField === "EUR"} onClick={() => handleCardClick("EUR")}
              flag="🇪🇺" symbol="€" name="אירו" value={eurResult} inputVal={inputVal}
              onInputChange={setInputVal} inputRef={inputRef} />}
          </div>
          {inputVal && <button type="button" onClick={() => setInputVal("")}
            className="w-full py-2 rounded-xl text-blue-500 hover:text-blue-300 text-xs transition-colors">✕ נקה</button>}
        </div>
      }
    </section>
  );
};

const TRIP_TABS = [
  { id: "flights", label: "טיסות", icon: "✈️" },
  { id: "itinerary", label: "מסלול ומלון", icon: "🗺️" },
  { id: "vault", label: "מסמכים", icon: "📁" },
  { id: "car", label: "רכב", icon: "🚗" },
  { id: "contacts", label: "אנשי קשר", icon: "👥" },
  { id: "expenses", label: "הוצאות", icon: "💰" },
  { id: "tips", label: "טיפים", icon: "💡" },
  { id: "calculator", label: "מטבע", icon: "🔄" },
];

const TripTabs = (props) => {
  const [activeTab, setActiveTab] = useState("flights");
  const tabIds = TRIP_TABS.map(t => t.id);
  const currentIdx = tabIds.indexOf(activeTab);
  const goNext = () => { if (currentIdx < tabIds.length - 1) setActiveTab(tabIds[currentIdx + 1]); };
  return (
    <div>
      <div className="flex overflow-x-auto gap-1 mb-4 pb-1 scrollbar-hide">
        {TRIP_TABS.map(tab => (
          <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all shrink-0
              ${activeTab === tab.id ? "bg-[#D4A017]/20 border border-[#D4A017]/50 text-[#D4A017]"
                : "bg-blue-900/30 border border-blue-700/30 text-blue-300 hover:text-white hover:bg-blue-800/40"}`}>
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>
      {activeTab === "flights" && (
        <FlightSection flights={props.flights} setFlights={props.setFlights} readOnly={props.readOnly}
          tripStart={props.tripStart} tripEnd={props.tripEnd} />
      )}
      {activeTab === "itinerary" && (
        <ItinerarySection days={props.days} setDays={props.setDays} tripId={props.tripId} readOnly={props.readOnly}
          sharedHotel={props.sharedHotel} setSharedHotel={props.setSharedHotel}
          sharedAccommodation={props.sharedAccommodation} setSharedAccommodation={props.setSharedAccommodation}
          setDocuments={props.setDocuments} />
      )}
      {activeTab === "vault" && (
        <VaultSection tripId={props.tripId} documents={props.documents} setDocuments={props.setDocuments} readOnly={props.readOnly} />
      )}
      {activeTab === "car" && (
        // ← days={props.days} מאפשר שיוך יום מהמסלול
        <CarSection tripId={props.tripId} car={props.car} setCar={props.setCar} readOnly={props.readOnly} days={props.days} />
      )}
      {activeTab === "contacts" && (
        <ContactsSection contacts={props.contacts} setContacts={props.setContacts} readOnly={props.readOnly} />
      )}
      {activeTab === "expenses" && (
        <ExpenseSection expenses={props.expenses} setExpenses={props.setExpenses}
          localCurrency={props.localCurrency} exchangeRates={props.exchangeRates} readOnly={props.readOnly}
          sharedAccommodation={props.sharedAccommodation} numTravelers={props.numTravelers} />
      )}
      {activeTab === "tips" && (
        <TipsSection tips={props.tips} setTips={props.setTips} readOnly={props.readOnly} destination={props.destination} />
      )}
      {activeTab === "calculator" && (
        <CurrencyCalculator localCurrency={props.localCurrency} setLocalCurrency={props.setLocalCurrency}
          exchangeRates={props.exchangeRates} loadingRates={props.loadingRates}
          ratesError={props.ratesError} readOnly={props.readOnly} />
      )}
      {!props.readOnly && currentIdx < tabIds.length - 1 && (
        <div className="flex mt-4" dir="ltr">
          <button type="button" onClick={goNext}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-yellow-500/15 border border-yellow-500/40
              text-yellow-400 hover:bg-yellow-500/25 hover:text-yellow-300 text-sm font-semibold transition-all">
            <span className="text-yellow-500/70">→</span>
            {TRIP_TABS[currentIdx + 1]?.icon} המשך ל{TRIP_TABS[currentIdx + 1]?.label}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── הרכיב הראשי ─────────────────────────────────────────
const TripForm = ({ initialData, onSaved, defaultReadOnly = false, onModeChange }) => {
  const [readOnly, setReadOnly] = useState(defaultReadOnly);
  const [printMode, setPrintMode] = useState(false);
  const [docId, setDocId] = useState(initialData?.docId || null);
  const [tripId, setTripId] = useState("");
  const [tripTitle, setTripTitle] = useState("");
  const [travelerName, setTravelerName] = useState("");
  const [travelerEmail, setTravelerEmail] = useState("");
  const [travelerPhone, setTravelerPhone] = useState("");
  const [destination, setDestination] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [days, setDays] = useState([]);
  const [sharedHotel, setSharedHotel] = useState(false);
  const [sharedAccommodation, setSharedAccommodation] = useState(null);
  const [car, setCar] = useState(null);
  const [flights, setFlights] = useState([
    { ...EMPTY_FLIGHT_LEG(), id: "outbound" },
    { ...EMPTY_FLIGHT_LEG(), id: "inbound", departureAirport: "", departureCity: "", arrivalAirport: "TLV", arrivalCity: "תל אביב" },
  ]);
  const [documents, setDocuments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [authCode, setAuthCode] = useState(generateCode);
  const [expenses, setExpenses] = useState([]);
  const [tips, setTips] = useState([]);
  const [numTravelers, setNumTravelers] = useState(1);
  const [localCurrency, setLocalCurrency] = useState("");
  const [exchangeRates, setExchangeRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [ratesError, setRatesError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [idError, setIdError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const validateEmail = (email) => {
    if (!email.trim()) return "אימייל הוא שדה חובה";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "כתובת אימייל לא תקינה";
    return "";
  };
  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, "");
    if (!digits) return "טלפון הוא שדה חובה";
    if (digits.length !== 10) return "טלפון חייב להכיל 10 ספרות";
    if (!digits.startsWith("05")) return "טלפון חייב להתחיל ב-05";
    return "";
  };

  useEffect(() => {
    if (initialData) {
      const d = addIdsToData(initialData);
      setTripId(d.tripId || ""); setTripTitle(d.tripTitle || "");
      setTravelerName(d.travelerName || ""); setTravelerEmail(d.travelerEmail || "");
      setTravelerPhone(d.travelerPhone || ""); setDestination(d.destination || "");
      setStartDate(d.startDate || ""); setEndDate(d.endDate || "");
      setDays(d.itinerary || []); setSharedHotel(!!d.sharedHotel);
      setSharedAccommodation(d.sharedAccommodation || null); setCar(d.car || null);
      setFlights(d.flights?.length
        ? d.flights.map(f => ({ ...EMPTY_FLIGHT_LEG(), ...f, id: f.id || Date.now() + Math.random() }))
        : [
          { ...EMPTY_FLIGHT_LEG(), id: "outbound" },
          { ...EMPTY_FLIGHT_LEG(), id: "inbound", departureAirport: "", departureCity: "", arrivalAirport: "TLV", arrivalCity: "תל אביב" },
        ]);
      setDocuments(d.documents || []); setContacts(d.contacts || []);
      setAuthCode(d.authCode || generateCode());
      setExpenses(d.expenses || []); setTips(d.tips || []);
      setLocalCurrency(d.localCurrency || (COUNTRIES.find(c => c.name === d.destination)?.currency) || "");
      setNumTravelers(d.numTravelers || 1);
    }
  }, [initialData]);

  useEffect(() => {
    const fetchRates = async () => {
      setLoadingRates(true); setRatesError("");
      try {
        const res = await fetch("https://api.exchangerate-api.com/v4/latest/ILS");
        const data = await res.json();
        const inverted = {};
        Object.entries(data.rates).forEach(([code, rate]) => { inverted[code] = 1 / rate; });
        setExchangeRates(inverted);
      } catch (err) { setRatesError("לא ניתן לטעון שערי מטבע — בדוק חיבור אינטרנט"); }
      finally { setLoadingRates(false); }
    };
    fetchRates();
  }, []);

  useEffect(() => {
    if (destination && !initialData?.localCurrency) {
      const match = COUNTRIES.find(c => c.name === destination);
      if (match?.currency) setLocalCurrency(match.currency);
    }
  }, [destination]);

  const handleStartDateChange = (val) => {
    setStartDate(val);
    if (endDate && val <= endDate) setDays(buildDaysFromDates(val, endDate));
    else if (endDate && val > endDate) { setEndDate(""); setDays([]); }
  };
  const handleEndDateChange = (val) => {
    setEndDate(val);
    if (startDate && val >= startDate) setDays(buildDaysFromDates(startDate, val));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!tripId.trim()) { setError("יש להזין מספר זהות"); return; }
    if (tripId.trim().length !== 9) { setIdError("מספר זהות חייב להכיל 9 ספרות בדיוק"); return; }
    if (!travelerName.trim()) { setError("יש להזין שם מטייל"); return; }
    const eErr = validateEmail(travelerEmail);
    const pErr = validatePhone(travelerPhone);
    if (eErr) { setEmailError(eErr); return; }
    if (pErr) { setPhoneError(pErr); return; }
    setSaving(true); setError("");
    try {
      const firestoreDocId = docId || `${tripId.trim()}_${Date.now()}`;
      if (!docId) setDocId(firestoreDocId);
      const tripData = {
        docId: firestoreDocId,
        tripId: tripId.trim(), tripTitle: tripTitle.trim(), travelerName: travelerName.trim(),
        travelerEmail: travelerEmail.trim(), travelerPhone: travelerPhone.trim(),
        destination: destination.trim(), startDate, endDate, authCode,
        flights: flights.map(({ id, ...rest }) => rest),
        itinerary: days.map(({ id, ...rest }) => ({
          ...rest, activities: rest.activities.map(({ id: aId, ...aRest }) => aRest),
        })),
        documents: documents.map(({ id, ...rest }) => rest),
        contacts: contacts.map(({ id, ...rest }) => rest),
        sharedHotel, sharedAccommodation: sharedAccommodation || null,
        car: car || null,
        expenses: expenses.map(e => ({ ...e })),
        tips: tips.map(t => ({ ...t })),
        numTravelers: numTravelers || 1,
        localCurrency: localCurrency || null,
        updatedAt: new Date().toISOString(),
        createdAt: initialData?.createdAt || new Date().toISOString(),
      };
      const sanitize = (obj) => JSON.parse(JSON.stringify(obj, (k, v) => v === undefined ? null : v));
      await setDoc(doc(db, "trips", firestoreDocId), sanitize(tripData), { merge: true });
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      if (onSaved) onSaved(tripData);
    } catch (err) { setError("שגיאה בשמירה: " + err.message); }
    finally { setSaving(false); }
  };

  return (
    <div dir="rtl" className={`transition-all duration-300 ${!readOnly ? "ring-2 ring-red-500/40 rounded-2xl p-1" : ""}`}>
      {!readOnly && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30">
          <span className="text-red-400 text-sm">✏️</span>
          <span className="text-red-400 text-sm font-semibold flex-1">מצב עריכה פעיל — שינויים יישמרו בלחיצה על שמור</span>
          {!tripId && (
            <button type="button" onClick={() => {
              const d = generateNOBData();
              setTripId(d.tripId); setTripTitle(d.tripTitle || '');
              setTravelerName(d.travelerName); setTravelerEmail(d.travelerEmail);
              setTravelerPhone(d.travelerPhone); setDestination(d.destination);
              setStartDate(d.startDate); setEndDate(d.endDate);
              setAuthCode(d.authCode); setLocalCurrency(d.localCurrency);
              setSharedHotel(d.sharedHotel); setSharedAccommodation(d.sharedAccommodation);
              setFlights(d.flights); setDays(d.itinerary);
              setContacts(d.contacts); setExpenses(d.expenses);
              setDocuments(d.documents); setTips(d.tips); setCar(d.car);
            }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600/20 border border-purple-500/40
                text-purple-300 hover:bg-purple-600/30 hover:text-purple-200 text-xs font-bold transition-all"
              title="NOB — מלא שדות בנתוני בדיקה רנדומליים">
              🎲 NOB
            </button>
          )}
        </div>
      )}
      {defaultReadOnly && (
        <div className="flex justify-end gap-2 mb-4">
          {readOnly && (
            <button type="button" onClick={() => setPrintMode(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200
                bg-blue-800/30 border border-blue-700/40 text-blue-300 hover:bg-blue-700/40 hover:text-white">
              <Printer size={16} /> הדפס תיק
            </button>
          )}
          {!readOnly && (
            <button type="button" onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200
                bg-gradient-to-r from-[#D4A017] to-[#F5C842] text-[#060F2A]
                hover:from-[#F5C842] hover:to-[#D4A017] disabled:opacity-60 shadow-lg shadow-[#D4A017]/20">
              {saving ? "שומר..." : "💾 שמור"}
            </button>
          )}
          <button type="button" onClick={() => { const next = !readOnly; setReadOnly(next); if (onModeChange) onModeChange(!next); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200
              ${readOnly
                ? "bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/30"
                : "bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30"}`}>
            {readOnly ? <><Edit3 size={16} /> עריכת תיק</> : <><Eye size={16} /> צפיה בתיק</>}
          </button>
        </div>
      )}

      {printMode && (
        <PrintView
          travelerName={travelerName} tripId={tripId} destination={destination}
          tripTitle={tripTitle} startDate={startDate} endDate={endDate}
          travelerPhone={travelerPhone} travelerEmail={travelerEmail}
          flights={flights} days={days} sharedHotel={sharedHotel}
          sharedAccommodation={sharedAccommodation} car={car}
          expenses={expenses} numTravelers={numTravelers}
          exchangeRates={exchangeRates} localCurrency={localCurrency}
          onClose={() => setPrintMode(false)} />
      )}

      <form onSubmit={handleSave} onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "BUTTON" && e.target.tagName !== "TEXTAREA") e.preventDefault(); }} className="space-y-6">
        <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
          <SectionTitle icon={User} title="פרטי הלקוח" subtitle="פרטי קשר של המטייל" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">מספר זהות <span className="text-red-400">*</span></label>
              <input placeholder="9 ספרות" value={tripId}
                readOnly={readOnly || !!initialData} inputMode="numeric" maxLength={9}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 9);
                  setTripId(v);
                  setIdError(v.length > 0 && v.length < 9 ? "מספר זהות חייב להכיל 9 ספרות" : "");
                }}
                onBlur={() => { if (tripId && tripId.length !== 9) setIdError("מספר זהות חייב להכיל 9 ספרות בדיוק"); }}
                className={`${inputCls(readOnly || !!initialData)} ${idError ? "border-red-500/70" : ""}`} required />
              {tripId && !readOnly && !initialData && (
                <div className="flex gap-1 mt-0.5">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < tripId.length ? "bg-yellow-400" : "bg-blue-800"}`} />
                  ))}
                </div>
              )}
              {idError && <div className="flex items-center gap-2 text-red-400 text-xs mt-1 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2">
                <AlertCircle size={14} /> {idError}</div>}
            </div>
            <InputField label={<>שם מלא <span className="text-red-400">*</span></>} placeholder="שם המטייל" value={travelerName} readOnly={readOnly}
              onChange={(e) => setTravelerName(e.target.value)} required />
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">אימייל <span className="text-red-400">*</span></label>
              <div className="relative">
                <Mail size={15} className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-500 pointer-events-none" />
                {readOnly
                  ? <div className={`${inputCls(true)} pr-10`}>{travelerEmail || "—"}</div>
                  : <input type="email" placeholder="דוגמה@gmail.com" value={travelerEmail} dir="ltr" required
                    onChange={(e) => { setTravelerEmail(e.target.value); setEmailError(validateEmail(e.target.value)); }}
                    onBlur={() => setEmailError(validateEmail(travelerEmail))}
                    className={`${inputCls(false)} pr-10 ${emailError ? "border-red-500/70 focus:border-red-500/70" : ""}`} />
                }
              </div>
              {emailError && !readOnly && (
                <div className="flex items-center gap-2 text-red-400 text-xs mt-1 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2">
                  <AlertCircle size={14} /> {emailError}
                </div>
              )}
              {travelerEmail && !readOnly && !emailError && (
                <a href={`mailto:${travelerEmail}`} className="text-blue-400 hover:text-[#D4A017] text-xs flex items-center gap-1 w-fit transition-colors">
                  <Mail size={12} /> שלח אימייל
                </a>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">טלפון <span className="text-red-400">*</span></label>
              {readOnly
                ? <div className={`${inputCls(true)} flex items-center gap-1`}><Phone size={15} className="text-blue-500" />{travelerPhone || "—"}</div>
                : <input type="tel" placeholder="05XXXXXXXX" value={travelerPhone} inputMode="numeric" maxLength={10} dir="ltr" required
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "");
                    const v = raw.startsWith("05") ? raw.slice(0, 10)
                      : raw.startsWith("0") ? "0" + raw.slice(1, 10)
                        : "05" + raw.slice(0, 8);
                    setTravelerPhone(v);
                    setPhoneError(v.length > 2 && v.length < 10 ? `נותרו ${10 - v.length} ספרות` : "");
                  }}
                  onFocus={() => { if (!travelerPhone) setTravelerPhone("05"); }}
                  onBlur={() => setPhoneError(validatePhone(travelerPhone))}
                  className={`${inputCls(false)} ${phoneError ? "border-red-500/70" : ""}`}
                />
              }
              {phoneError && !readOnly && (
                <div className="flex items-center gap-2 text-red-400 text-xs mt-1 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2">
                  <AlertCircle size={14} /> {phoneError}
                </div>
              )}
              {travelerPhone && !readOnly && !phoneError && travelerPhone.length === 10 && (
                <a href={`https://wa.me/972${travelerPhone.slice(1)}`} target="_blank" rel="noopener noreferrer"
                  className="text-green-400 hover:text-green-300 text-xs flex items-center gap-1 w-fit transition-colors">
                  <MessageCircle size={12} /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </section>

        <section className="bg-blue-950/60 border border-blue-800/50 rounded-2xl p-6 backdrop-blur-sm">
          <SectionTitle icon={MapPin}
            title={tripTitle ? tripTitle : "פרטי הטיול"}
            subtitle={tripTitle ? `${destination || ""}` : "יעד ותאריכים"} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">כותרת הטיול</label>
              <div className="text-blue-500 text-xs mb-1">יוצג בראש התיק ובאפליקציה</div>
              {readOnly
                ? <div className={inputCls(true)}>{tripTitle || "—"}</div>
                : <input placeholder='לדוגמה: "חופשת קיץ ברומא 2026"' value={tripTitle}
                  onChange={(e) => setTripTitle(e.target.value)} className={inputCls(false)} />
              }
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">יעד הטיול</label>
              {readOnly
                ? <div className={`${inputCls(true)} flex items-center gap-2`}>
                  <span className="text-xl">{COUNTRIES.find(c => c.name === destination)?.flag || "🌍"}</span>
                  <span>{destination || "—"}</span>
                </div>
                : <DestinationSearch value={destination} onChange={setDestination} />
              }
            </div>
            <div />
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">תאריך יציאה</label>
              {readOnly
                ? <div className={inputCls(true)}>{startDate ? startDate.split("-").reverse().join("/") : "—"}</div>
                : <EuDateInput value={startDate} onChange={handleStartDateChange} />
              }
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">תאריך חזרה</label>
              {readOnly
                ? <div className={inputCls(true)}>{endDate ? endDate.split("-").reverse().join("/") : "—"}</div>
                : <EuDateInput value={endDate} min={startDate} onChange={handleEndDateChange} />
              }
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-blue-200 text-sm font-medium">👥 מספר משתתפים</label>
              {readOnly
                ? <div className={inputCls(true)}>{numTravelers} {numTravelers === 1 ? "מטייל" : "מטיילים"}</div>
                : <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setNumTravelers(n => Math.max(1, n - 1))}
                    className="w-10 h-10 rounded-xl bg-blue-900/40 border border-blue-700/50 text-white text-xl font-bold hover:border-yellow-500/70 transition-all flex items-center justify-center">−</button>
                  <span className="text-white text-xl font-black w-8 text-center">{numTravelers}</span>
                  <button type="button" onClick={() => setNumTravelers(n => Math.min(20, n + 1))}
                    className="w-10 h-10 rounded-xl bg-blue-900/40 border border-blue-700/50 text-white text-xl font-bold hover:border-yellow-500/70 transition-all flex items-center justify-center">+</button>
                  <span className="text-blue-400 text-sm">{numTravelers === 1 ? "מטייל יחיד" : `${numTravelers} מטיילים`}</span>
                </div>
              }
            </div>
          </div>
        </section>

        <SecuritySection tripId={tripId} docId={docId} initialData={initialData} authCode={authCode} setAuthCode={!readOnly ? setAuthCode : undefined} />

        {!readOnly && (() => {
          const tripDetailsFilled = tripId.trim().length === 9 && travelerName.trim() && destination && startDate && endDate;
          return (
            <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all
              ${tripDetailsFilled ? "bg-green-500/10 border-green-500/30" : "bg-yellow-500/5 border-yellow-500/20"}`}>
              <span className={`text-lg ${tripDetailsFilled ? "text-green-400" : "text-yellow-500"}`}>
                {tripDetailsFilled ? "✅" : "⚠️"}
              </span>
              <span className={`text-sm ${tripDetailsFilled ? "text-green-400" : "text-yellow-400"}`}>
                {tripDetailsFilled
                  ? "פרטי הטיול מלאים — ניתן להמשיך לפרטי טיסה ושאר הטאבים"
                  : "יש להשלים: מ.ז, שם, יעד, תאריכי יציאה וחזרה"}
              </span>
            </div>
          );
        })()}

        <TripTabs
          flights={flights} setFlights={setFlights}
          days={days} setDays={setDays}
          tripId={tripId} readOnly={readOnly}
          tripStart={startDate} tripEnd={endDate}
          sharedHotel={sharedHotel} setSharedHotel={setSharedHotel}
          sharedAccommodation={sharedAccommodation} setSharedAccommodation={setSharedAccommodation}
          documents={documents} setDocuments={setDocuments}
          car={car} setCar={setCar}
          contacts={contacts} setContacts={setContacts}
          expenses={expenses} setExpenses={setExpenses}
          tips={tips} setTips={setTips}
          localCurrency={localCurrency} setLocalCurrency={!readOnly ? setLocalCurrency : undefined}
          exchangeRates={exchangeRates} loadingRates={loadingRates} ratesError={ratesError}
          destination={destination} numTravelers={numTravelers}
        />

        {error && <div className="flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          <AlertCircle size={18} /> {error}</div>}
        {saved && <div className="flex items-center gap-2 text-green-400 bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3">
          <CheckCircle size={18} /> תיק הטיול נשמר בהצלחה!</div>}

        {!readOnly && (
          <button type="submit" disabled={saving}
            className="w-full py-4 rounded-xl font-bold text-[#060F2A] text-lg
              bg-gradient-to-r from-[#D4A017] to-[#F5C842] hover:from-[#F5C842] hover:to-[#D4A017]
              disabled:opacity-60 disabled:cursor-not-allowed shadow-xl shadow-[#D4A017]/30
              transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99]">
            {saving ? "שומר..." : "💾 שמור תיק טיול"}
          </button>
        )}
      </form>
    </div>
  );
};

export default TripForm;